import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from 'ra-language-english';

/* ─── English (base + custom keys) ─── */
const en = {
  ...englishMessages,
  ra: {
    ...englishMessages.ra,
    page: {
      ...englishMessages.ra.page,
      dashboard: 'Dashboard',
    },
  },
  resources: {
    accounts: { name: 'Account |||| Accounts', fields: { 'accountResponse.accountName': 'Account Name', 'accountResponse.accountStatus': 'Status', 'licenseResponse.licenseName': 'License', validityTimeFrame: 'Validity', isLicenseBlocked: 'Blocked', 'accountResponse.rateLimitApplies': 'Rate Limit', 'accountResponse.loggingStatus': 'Log', primaryServer: 'Primary Server', secondaryServer: 'Secondary Server', 'accountResponse.allowDebugOutput': 'Diagnostic output', 'accountResponse.allowDebugOutputHelp': 'Allows this account to receive internal diagnostic fields in API responses (only when explicitly requested).' } },
    servers: { name: 'Server |||| Servers', fields: { name: 'Server Name', port: 'Port', url: 'Server URL', status: 'Status', isForcedOffline: 'Force Offline', apiKey: 'API Key' } },
    licenses: { name: 'License |||| Licenses', fields: { licenseName: 'License Name', servers: 'Servers' } },
    'document-types': { name: 'Document Type |||| Document Types', fields: { name: 'Name', mappingsCount: 'OCR Extraction', createdAt: 'Created', configured: 'rules', notConfigured: 'Not configured', mappingsTooltipYes: 'This document type has %{count} rules that define which fields the OCR engine extracts (e.g. name, ID number, expiry date)', mappingsTooltipNo: 'No extraction rules defined yet — the OCR engine won\'t know which fields to extract from this document type' } },
    nationalities: { name: 'Nationality |||| Nationalities', fields: { name: 'Name', mappingsCount: 'OCR Extraction', createdAt: 'Created', configured: 'rules', notConfigured: 'Not configured', mappingsTooltipYes: 'This nationality has %{count} extraction rules that define how the OCR engine processes documents from this country', mappingsTooltipNo: 'No extraction rules defined yet for this nationality' } },
    'kyc-fields': { name: 'KYC Field |||| KYC Fields', fields: { name: 'Name', usageCount: 'Usage', createdAt: 'Created', usedIn: 'Used in %{count} mappings', unused: 'Unused', usageTooltipYes: 'This KYC field is referenced in %{count} default mappings', usageTooltipNo: 'This KYC field is not used in any default mapping' } },
    users: { name: 'User |||| Users', fields: { name: 'User Name', email: 'Email', role: 'Role', isExternalUser: 'External User', password: 'Password' } },
    'fields-mapping': { name: 'Field Mapping |||| Field Mappings', fields: { document: 'Document', side: 'Side', standardFields: 'Standard Fields', fieldsCount: 'Fields', standardField: 'Standard field' } },
    'default-mapping': { name: 'Default Mapping |||| Default Mappings', fields: { standardFieldName: 'Standard Field', 'kycFields.name': 'KYC Field', priority: 'Priority' } },
    statistics: { name: 'Statistics' },
    'cost-centers': { name: 'Cost Center |||| Cost Centers', fields: { name: 'Name', description: 'Description', unpaidCreditLimit: 'Unpaid Credit Limit', isCreditsBlocked: 'Credits Blocked' } },
  },
  menu: {
    kyc_config: 'KYC Config',
    billing: 'Billing',
    system: 'System',
    backups: 'Backups',
    credits: 'Credits',
    pricing: 'Pricing',
    photo_upload: 'Test',
    ocr_testing: 'OCR Testing',
    developers: 'Developers',
  },
  shell: {
    environment: { nonProduction: 'TEST ENV', local: 'LOCAL' },
    impersonation: {
      active: 'Impersonating %{name}',
      exit: 'Exit',
    },
    breadcrumb: { label: 'Breadcrumb', detail: 'Detail' },
    rail: { label: 'Main navigation' },
    profile: { menu: 'Account menu', language: 'Language', session: 'Session: %{time}' },
    empty: { title: 'No results found', hint: 'Adjust the search or the filters' },
    sectionHeader: { more: 'More actions', moreCreate: 'Other ways to create' },
    rowActions: { more: 'Actions' },
    code: { copy: 'Copy', copied: 'Copied to the clipboard' },
    form: { create: 'Create %{name}', edit: 'Edit %{name}' },
    exportDialog: { title: 'Export', json: 'JSON: the file Import reads back', csv: 'CSV: for a spreadsheet' },
  },
  roles: { Administrator: 'Administrator', AdvancedUser: 'Advanced user', User: 'User', NoAccess: 'No access', Developer: 'Developer' },
  developers: {
    tabs: {
      overview: 'Getting started',
      reference: 'API reference',
      recipes: 'Code samples',
      downloads: 'Postman & OpenAPI',
    },
    operations: '%{smart_count} operation |||| %{smart_count} operations',
    quickStart: 'Quick start',
    environments: 'Environments',
    thisPanel: 'This panel',
    environmentsNote: 'Build and test your integration against the sandbox before moving to production.',
    assistant: {
      title: 'Working with an AI assistant',
      text: 'Download the OpenAPI file and give it to your coding assistant or to a client generator: it is the exact contract of the version deployed right now, with an example for every request.',
      action: 'Go to the downloads',
    },
    loading: 'Loading…',
    loadingReference: 'Loading the reference…',
    samplesIntro: 'The samples point at `%{url}`, the environment this panel runs against, and read your API key from the environment variable `%{variable}`: set it before running them. They read the images from files next to the sample.',
    language: 'Language',
    postman: {
      title: 'Postman',
      text: 'Every operation of the reference as a request ready to send, with the body filled in and the credentials in variables.',
      collection: 'Download the collection',
      step1: 'Import the collection and one environment file into Postman.',
      step2: 'Select the environment and set %{variables}.',
      step3: 'Open %{request}, put one of your own images in the body and press Send.',
    },
    openapi: {
      title: 'OpenAPI',
      text: 'The OpenAPI 3.1 specification of the version deployed right now%{version}. Generate a typed client from it, import it into Insomnia, Bruno or Hoppscotch, or give an AI coding assistant the exact contract.',
      download: 'Download the OpenAPI file',
    },
    errors: {
      forbidden: 'Your user does not have access to the KYC API documentation. Ask your Roommatik contact for it.',
      unavailable: 'The API specification is not available right now (%{status}). Getting started and the code samples do not depend on it.',
      viewer: 'The API reference could not be loaded. Reload the page to try again.',
      download: 'The download could not be completed (%{status}). Try again in a moment.',
    },
    noAccess: {
      pageTitle: 'No access',
      title: 'Your user does not have access to this panel',
      hint: 'Ask the administrator of your KYC account, or your Roommatik contact, for access to the panel or to the API documentation.',
    },
  },
  // Per-screen copy introduced by the 3c Hybrid redesign: one key per screen.
  screens: {
    pricing: {
      subtitle: 'Credits each operation consumes for a cost centre',
      pickHint: 'The rates of its operations appear here',
      help: 'Help',
      helpTitle: 'Pricing — how it works',
      helpIntro: 'The rates define how many credits each operation type consumes for a cost centre. Where no rate is set, the default applies.',
      helpDefaults: 'The defaults are shown as a guide. You can set a different rate for each cost centre.',
      operationTypes: 'Operation types',
      operations: {
        KycSingleSide: { name: 'KYC, one side', description: 'KYC validation of one side of the document' },
        KycDualSide: { name: 'KYC, both sides', description: 'KYC validation of both sides of the document' },
        MrzSingleSide: { name: 'MRZ, one side', description: 'MRZ reading of one side' },
        MrzDualSide: { name: 'MRZ, both sides', description: 'MRZ reading of both sides' },
      },
      credits: '%{count} credit |||| %{count} credits',
      byOperation: 'Rates by operation',
      tableScope: 'An operation without its own rate uses the default',
      operationType: 'Operation type',
      creditsPerOperation: 'Credits per operation',
      creditsFor: 'Credits per operation for %{name}',
      state: 'Rate',
      custom: 'Custom',
      default: 'Default',
      defaultValue: 'Default: %{count} credit |||| Default: %{count} credits',
      defaultShort: '%{count} (default)',
      edit: 'Edit rate',
      restore: 'Restore default',
      restoreTitle: 'Restore the default rate?',
      restoreBody: '%{name} will consume the default of %{count} credit again for this cost centre. |||| %{name} will consume the default of %{count} credits again for this cost centre.',
      restoreConfirm: 'Restore',
      saved: 'Rate saved',
      saveFailed: 'The rate could not be saved',
      restored: 'Rate removed; the default applies',
      restoreFailed: 'The rate could not be removed',
    },
    billing: {
      costCenter: 'Cost centre',
      pickCostCenter: 'Select a cost centre',
    },
    credits: {
      subtitle: 'Balance, movements and consumption of a cost centre',
      pickHint: 'Its balance, transactions and monthly summary appear here',
      help: 'Help',
      helpTitle: 'Credits — how they work',
      helpIntro: "Each cost centre has a credit balance that its accounts consume automatically when they run KYC operations. The cost of each operation depends on the centre's pricing.",
      helpAvailableTitle: 'Available credits',
      helpAvailable: 'Current balance, ready to be consumed.',
      helpReservedTitle: 'Reserved credits',
      helpReserved: 'Credits held temporarily by operations in progress.',
      helpTopUpTitle: 'Top up',
      helpTopUp: "Adds credits to the centre's balance. It is recorded as a top-up transaction.",
      helpAdjustmentTitle: 'Manual adjustment',
      helpAdjustment: 'Adds or removes credits with a reason. Negative values subtract from the balance.',
      topUp: 'Top up credits',
      adjust: 'Manual adjustment',
      available: 'Available credits',
      reserved: 'Reserved credits',
      status: 'Status',
      active: 'Active',
      blocked: 'Blocked',
      blockedDetail: 'Its accounts cannot consume credits',
      transactions: 'Transaction history',
      noTransactions: 'No transactions recorded',
      noTransactionsHint: 'Top-ups, refunds, consumption and adjustments appear here',
      date: 'Date',
      type: 'Type',
      amount: 'Amount',
      balanceAfter: 'Balance after',
      types: { TOPUP: 'Top-up', REFUND: 'Refund', DEDUCT: 'Consumption', MANUAL_ADJ: 'Manual adjustment' },
      monthly: 'Monthly summary',
      previousYear: 'Previous year',
      nextYear: 'Next year',
      noMonthly: 'No data for %{year}',
      noMonthlyHint: 'Change the year with the arrows',
      month: 'Month',
      operations: 'Operations',
      creditsConsumed: 'Credits consumed',
      events: 'Event log',
      noEvents: 'No events recorded',
      noEventsHint: 'Each operation that consumes credits appears here',
      operationType: 'Operation type',
      topUpTitle: 'Top up credits — %{name}',
      topUpHint: "Enter the number of credits to add to the cost centre's balance.",
      topUpConfirm: 'Top up',
      topUpDone: 'Top-up completed',
      topUpFailed: 'The top-up could not be completed',
      adjustTitle: 'Manual adjustment — %{name}',
      adjustHint: 'Positive values add credits, negative values subtract them. A reason is required.',
      adjustConfirm: 'Apply adjustment',
      adjustmentDone: 'Adjustment applied',
      adjustmentFailed: 'The adjustment could not be applied',
      quantity: 'Amount',
      reason: 'Reason',
      reasonHelp: 'Required. Explain why the balance is adjusted.',
    },
    statistics: {
      subtitle: 'Successful and failed requests of every account in the period',
      period: 'Period',
      periodColumn: 'Successful / failed (%{period})',
      okErr: '%{ok} / %{err}',
      successful: 'Successful',
      errorRate: 'Error rate',
      api: 'API',
      sortBy: 'Sort by',
      sortAsc: '%{field}, ascending',
      sortDesc: '%{field}, descending',
      resultCount: '%{count} account |||| %{count} accounts',
      prefixKiosk: 'KR — Kiosks',
      prefixOnline: 'OC — Online check-in',
      loadFailed: 'The statistics could not be loaded',
      loadFailedHint: 'Refresh to try again',
    },
    stressTest: {
      subtitle: 'Concurrent requests through the gateway, with the latency and errors they produce',
      startFailed: 'The test could not be run: %{error}',
      rps: 'Requests/s',
      avg: 'Average latency',
      rateBase: '%{failed} of %{total} requests',
      minMax: 'Min / max latency',
      statusCode: 'HTTP code',
      network: 'Network',
      bucketTooltip: '%{bucket} ms: %{count} requests',
      bucketAxis: 'Latency (ms)',
      placeholderHint: 'The results appear here when the test finishes',
      historyCount: '%{smart_count} test |||| %{smart_count} tests',
      showHistory: 'Show',
      hideHistory: 'Hide',
    },
    techSupport: {
      overviewSubtitle: 'Gateway, KYC servers and the error rate of the last 7 days',
      loadFailed: 'The system information could not be loaded. Refresh to try again.',
      health: { healthy: 'Healthy', degraded: 'Degraded', down: 'Down' },
      online: 'Online',
      offline: 'Offline',
      accountsDetail: 'registered',
      lastSevenDays: 'last 7 days',
      noRequestsWeek: 'No requests in the last 7 days',
      primary: 'Primary',
      secondary: 'Secondary',
      ocrCheck: '%{role} OCR server — %{name}',
      faceMatch: 'Face comparison',
      miDniQr: 'miDNI QR',
      available: 'Available',
      unavailable: 'Unavailable',
      unknown: 'Not reported',
      servicesSubtitle: 'What the gateway reports and the response time of each registered server',
      registeredServers: 'Registered servers',
      registeredCount: '%{smart_count} server |||| %{smart_count} servers',
      port: 'Port %{port}',
      notMeasured: 'Not measured',
      unreachable: 'Not responding',
      serversEmptyHint: 'Servers are added in System › Servers',
    },
    dashboard: {
      subtitle: 'Requests of every account and the resources configured',
      requestsDetail: '%{ok} successful · %{err} failed',
      rateBase: '%{err} of %{total} requests failed',
      noRequestsToday: 'No requests today yet',
      requestsTodayLower: 'requests today',
      noRecentActivity: 'No activity in the last 7 days',
      weekByAccount: 'Requests by account',
      weekByAccountScope: 'Last 7 days · the %{count} busiest accounts',
      barLabel: '%{total} (%{err} failed)',
      barAria: '%{name}: %{ok} successful, %{err} failed',
      noWeekRequests: 'No requests in the last 7 days',
      noWeekRequestsHint: 'Accounts appear here after their first request',
      resourcesScope: 'Records configured in each section',
      activityScope: 'Every account, busiest today first',
      today: 'Today',
      okErr: '%{ok} / %{err}',
      noActivity: 'No account has activity yet',
      noActivityHint: 'Accounts appear here after their first request',
      statsUnavailable: 'The statistics could not be loaded',
      statsUnavailableHint: 'Refresh to try again',
    },
    documentTypes: {
      subtitle: 'Documents the OCR engine recognises, and which fields it reads from each',
      usesDefault: 'Uses the default configuration',
      more: '(+%{count} more)',
    import: 'Import',
    importDialog: {
      title: 'Import mappings',
      records: 'The file holds %{smart_count} record |||| The file holds %{smart_count} records',
      replace: 'Replace every existing mapping',
      replaceHint: 'When ticked, every current record is deleted and replaced by those in the file.',
      replaceWarning: 'Replace: every current mapping is deleted before importing. This cannot be undone.',
      mergeInfo: 'Merge: existing records are updated, new ones are added and the rest stay as they are.',
      resultTitle: 'Import result',
      total: 'Processed',
      created: 'Created',
      updated: 'Updated',
      skipped: 'Unchanged',
      deleted: 'Deleted',
      errors: '%{smart_count} error during the import |||| %{smart_count} errors during the import',
      errorRow: 'Row %{row}: %{message}',
      importing: 'Importing…',
      run: 'Import',
      runReplace: 'Replace and import',
      invalidFile: 'The file could not be read',
      emptyFile: 'The file is empty or its format is not recognised',
      summary: 'Import finished: %{created} created, %{updated} updated, %{skipped} unchanged, %{deleted} deleted, %{errors} errors',
      failed: 'The import could not be completed',
    },
    },
    nationalities: {
      subtitle: 'Countries whose documents the OCR engine reads, and which document types are set up for each',
      documents: 'Documents',
    },
    defaultMappings: {
      subtitle: 'Which KYC field fills each standard field, and in what order they are tried',
      help: 'How it works',
      helpQr: 'QR fields (miDNI) are listed apart because they come only from the QR code of the Spanish electronic ID and do not compete with the fields read from the document. They always have priority 0.',
      priorityHelp: '0 = highest priority. When several KYC fields fill the same standard field, the lowest number is used.',
      priorityDecrease: 'Lower the number (higher priority)',
      priorityIncrease: 'Raise the number (lower priority)',
      prioritySave: 'Save priority',
      priorityUpdated: 'Priority updated',
      priorityFailed: 'The priority could not be updated',
      qrTitle: 'QR fields (miDNI) · %{count}',
      qrInfo: 'These fields come only from the QR code of the Spanish electronic ID (miDNI). They do not compete with the fields read from the document, are always returned when the QR code is present and need no priority.',
      qrField: 'QR field',
      exported: 'Export complete (%{smart_count} record) |||| Export complete (%{smart_count} records)',
      exportFailed: 'The export could not be completed',
      sources: {
        MRZ: 'MRZ — machine readable zone',
        NORM: 'NORM — normalised data',
        VIZ: 'VIZ — visual inspection zone',
        QR: 'QR — QR code data',
      },
    },
    fieldMappings: {
      subtitle: 'How the OCR engine turns what it reads from each document and side into the standard response',
      help: 'How it works',
      exported: 'Export complete (%{smart_count} record) |||| Export complete (%{smart_count} records)',
      exportFailed: 'The export could not be completed',
      documentInfo: 'Document',
      documentType: 'Document type',
      nationality: 'Nationality',
      documentSide: 'Document side',
      mappingFields: 'Mapping rules',
      pickNationalityFirst: 'Select a nationality first',
      regexMark: 'Regex: %{regex}',
      sideBoth: 'Both sides',
      conflictCount: '%{smart_count} conflict |||| %{smart_count} conflicts',
      editor: {
        ruleCount: '%{smart_count} rule |||| %{smart_count} rules',
        showExample: 'See an example',
        addRule: 'Add rule',
        addRuleHint: 'Add a new mapping rule',
        completeRuleFirst: 'Complete the current rule before adding another',
        duplicates: 'Some output field names are repeated. Each one must be unique.',
        noRules: 'There are no mapping rules yet. Select "Add rule" to define how what the OCR engine reads becomes the normalised response.',
        ruleNumber: 'Rule %{n}',
        oneRuleRequired: 'At least one rule is required',
        removeRule: 'Remove this rule',
        output: 'Output',
        outputInfo: 'The output field that appears in the normalised response. It is the standard name the consumers of the service receive, whatever document was scanned.',
        normalisedField: 'Normalised response field',
        normalisedFieldInfo: 'Name of the field in the normalised response, for example surname, documentNumber or dateOfBirth.',
        normalisedFieldPlaceholder: 'e.g. surname',
        duplicateName: 'This name is repeated',
        regex: 'Regex transformation (optional)',
        regexInfo: 'An optional regular expression applied to the extracted value before it is returned. For example, ^[A-Z\\s]+ keeps only capital letters and spaces, filtering out unexpected characters in a name.',
        regexPlaceholder: 'e.g. ^[A-Z\\s]+',
        regexTester: 'Regex tester',
        regexTesterInfo: 'Try the regular expression live: enter a sample value as the OCR engine would return it and see what the transformation gives back.',
        regexTesterPlaceholder: 'Enter a sample value to try',
        regexResult: 'Result:',
        regexNoMatch: '(no match)',
        regexInvalid: 'Invalid regex: %{message}',
        sources: 'Sources',
        sourcesInfo: 'Where the value of this field comes from: a field the OCR engine extracts from the document (the usual case) or a fixed value returned every time. With several sources they are tried in order and the first one with a value is used.',
        sourcesOrder: 'Tried in order; the first with a value wins',
        sourcePosition: 'Source %{n}',
        sourceKind: 'Kind of source',
        sourceOcr: 'OCR',
        sourceFixed: 'Fixed',
        oneSourceRequired: 'At least one source is required',
        removeSource: 'Remove source',
        ocrField: 'OCR field',
        ocrFieldInfo: 'The field of the OCR engine the value is taken from, such as MRZ_Surname or VIZ_DocumentNumber. Each one is a specific zone and piece of data of the document.',
        ocrFieldPlaceholder: 'Select a field',
        prefix: 'Prefix',
        prefixHelp: 'Optional, added before',
        suffix: 'Suffix',
        suffixHelp: 'Optional, added after',
        fixedValue: 'Fixed value',
        fixedValueInfo: 'Literal text always returned as the value of this field, whatever the OCR engine reads. Useful for values that are constant for a document type, such as P for a passport.',
        fixedValuePlaceholder: 'e.g. P, ID_CARD',
        addSource: 'Add fallback source',
        addSourceHint: 'Add another source to try when the ones above are empty',
        completeSourcesFirst: 'Complete the existing sources before adding another',
        helpTitle: 'Field mapping: how it works',
        helpIntro: 'Each rule defines how an output field of the response is built from the raw data the OCR engine reads. The engine extracts values under internal names such as MRZ_Surname or VIZ_DocumentNumber; the mapping turns them into clean, standard names such as surname or documentNumber.',
        exampleTitle: 'A complete example',
        exampleOutput: 'the name of the field in the normalised response',
        exampleSources: 'Where the value is taken from, in order of priority:',
        exampleMrz: 'machine readable zone',
        exampleViz: 'visual inspection zone',
        exampleNorm: 'normalised, front side',
        exampleExplanation: 'The sources are tried in order. If MRZ_Surname is empty (a damaged MRZ), VIZ_Surname is tried, then NORM_FRONT_ENG_Surname. The first source with a value wins and is returned as surname.',
      },
    },
    kycFields: {
      subtitle: 'Values the OCR engine extracts from a document, and which standard fields read each one',
      usedBy: 'Used by',
      used: 'In use',
    },
    servers: {
      subtitle: 'Recognition servers that process the documents, and whether they are reachable',
      server: 'Server',
      portLine: 'Port %{port}',
      status: { Online: 'Online', Offline: 'Offline' },
      forcedOffline: 'Forced offline',
      urlHint: 'For example https://hostname',
      sectionConnection: 'Connection',
      sectionAccess: 'Access',
      copyKey: 'Copy the API key',
      keyCopied: 'API key copied to the clipboard',
    },
    licenses: {
      subtitle: 'Licenses and the servers each one may use',
      serverCount: 'Number of servers',
      noServers: 'No servers',
      sectionLicense: 'License',
      sectionServers: 'Servers it may use',
    },
    users: {
      subtitle: 'People who can sign in to this console, and their role',
      user: 'User',
      inheritedRoles: 'Additional permissions inherited from a group. To change them, manage the group membership in the identity provider.',
      inheritedNotice: 'This user has additional permissions inherited from a group. Changing the role here changes only their direct assignment; group membership is managed in the identity provider and may keep a higher effective role.',
      createInfo: 'The account is created in the corporate identity provider. The user will receive an email to set a password and verify the address; this application stores no password.',
      fullName: 'Full name',
      fullNameHint: 'First name and surname',
      emailLocked: 'The email is the unique identifier in the identity provider and cannot be changed here.',
      sectionPerson: 'The person',
      sectionRole: 'Role',
    },
    backups: {
      subtitle: 'Scheduled copies of the database, and restoring from one',
    },
    costCenters: {
      subtitle: 'Groups of accounts that share one credit balance',
      name: 'Cost center',
      unpaidLimit: 'Unpaid limit',
      noLimit: 'No limit',
      credits: '%{count} credits',
      available: 'Available credits',
      reserved: '%{count} reserved',
      status: 'Status',
      active: 'Active',
      blocked: 'Blocked',
      block: 'Block credits',
      unblock: 'Unblock credits',
      blockedDone: 'Credits blocked',
      unblockedDone: 'Credits unblocked',
      blockFailed: 'The status could not be changed',
      help: 'How it works',
      helpTitle: 'Cost centers: how they work',
      helpIntro: 'A cost center groups one or more accounts under the same billing. Each center has its own credit balance, which is used when its accounts run KYC operations.',
      helpLimitTitle: 'Unpaid credit limit',
      helpLimit: 'The most credits that can be used before paying. When the limit is reached, operations are blocked.',
      helpStatusTitle: 'Credit status',
      helpStatus: 'Blocks or unblocks the credits of a center by hand. While blocked, none of its accounts can run operations.',
      helpAccountsTitle: 'Assigned accounts',
      helpAccounts: 'Accounts assigned to a cost center use credits from its balance. An account can belong to one cost center only.',
      sectionDetails: 'The cost center',
      unpaidLimitHint: 'The most credits that can be used without paying. 0 = no limit.',
      accountsTitle: 'Assigned accounts',
      accountsHint: 'Assigned accounts use credits from this cost center when they run KYC operations.',
      account: 'Account',
      selectAccount: 'Select an account',
      assign: 'Assign',
      unassign: 'Unassign %{name}',
      assigned: 'Account assigned',
      assignFailed: 'The account could not be assigned',
      unassigned: 'Account unassigned',
      unassignFailed: 'The account could not be unassigned',
      noAccounts: 'No accounts are assigned to this cost center.',
    },
    accounts: {
      subtitle: 'Customer accounts that use the service, their license and the servers they run on',
      status: { Active: 'Active', Limited: 'Limited', Blocked: 'Blocked' },
      logging: { None: 'Nothing', Wrong: 'Failed requests', All: 'All requests' },
      loggingLabel: 'What is logged',
      rateOff: 'No limit',
      rate: '%{min}/min, %{day}/day',
      perMin: 'Requests per minute',
      perDay: 'Requests per day',
      rateLimitApplies: 'Apply request limits',
      licenseBlocked: 'License blocked',
      apiKey: 'API key',
      sectionAccount: 'The account',
      sectionAccess: 'Access',
      sectionLicense: 'License',
      sectionServers: 'Servers',
      sectionLimits: 'Request limits',
      sectionLogging: 'Logging and diagnostics',
    },
    ocrTesting: {
      casesTitle: 'Test cases',
      casesSubtitle: 'Documents with known values that the OCR engine is checked against',
      runTests: 'Run tests',
      newCase: 'New case',
      runStarted: 'Test run started',
      runStartFailed: 'The test run could not be started',
      runAlreadyInProgress: 'A test run is already in progress. Wait for it to finish.',
      loadCasesFailed: 'The test cases could not be loaded',
      noCases: 'No test cases found',
      noCasesHint: 'Adjust the filters or create a new case',
      name: 'Name',
      documentType: 'Document type',
      nationality: 'Nationality',
      operationType: 'Operation type',
      operation: 'Operation',
      status: 'Status',
      created: 'Created',
      all: 'All',
      validated: 'Validated',
      draft: 'Draft',
      runsTitle: 'Test runs',
      runsSubtitle: 'Each run checks the test cases against the OCR engine and records what matched',
      loadRunsFailed: 'The runs could not be loaded',
      noRuns: 'No test runs yet',
      noRunsHint: 'Start one with "Run tests" in Test cases',
      date: 'Date',
      sdkVersion: 'SDK version',
      total: 'Total',
      passed: 'Passed',
      failed: 'Failed',
      errors: 'Errors',
      failedErrors: 'Failed / errors',
      passRate: 'Pass rate',
      passedOf: '%{rate}% (%{passed}/%{total})',
      passedOfTotal: '%{passed}/%{total} passed',
      duration: 'Duration',
      running: 'Running…',
      completed: 'Completed',
      runTitle: 'Run of %{date}',
      runNotFound: 'Run not found',
      runNotFoundHint: 'It may have been deleted; go back to the list of runs',
      loadResultsFailed: 'The results could not be loaded',
      sdkValue: 'SDK %{version}',
      durationValue: 'Duration: %{value}',
      results: 'Results',
      show: 'Show',
      showAll: 'All (%{count})',
      showFailed: 'Failed (%{count})',
      showErrors: 'Errors (%{count})',
      noResults: 'No results to show',
      noResultsHint: 'Change what the list shows, or wait for the run to finish',
      testCase: 'Test case',
      timeMs: 'Time (ms)',
      timeValue: '%{ms} ms',
      showFields: 'Show fields',
      hideFields: 'Hide fields',
      noFieldDetail: 'No field detail available',
      fieldResults: 'Field comparison',
      field: 'Field',
      expected: 'Expected',
      actual: 'Read',
      mode: 'Mode',
      result: 'Result',
      matched: 'Matches',
      mismatched: 'Does not match',
      createTitle: 'New test case',
      createSubtitle: 'A document image and the values the OCR engine should read from it',
      caseInfo: 'The case',
      info: 'Information',
      description: 'Description',
      nationalityHelp: '3-letter code (e.g. ESP)',
      images: 'Images',
      imagesHint: 'The front is required; the back only for operations that read both sides',
      front: 'Front',
      back: 'Back',
      dropImage: '%{side}: drop an image here or click to select one',
      removeImage: 'Remove',
      nameRequired: 'The name is required',
      frontRequired: 'Upload at least the front image',
      caseCreated: 'Test case created',
      caseCreateFailed: 'The test case could not be created',
      creating: 'Creating…',
      createCase: 'Create case',
      caseNotFound: 'Test case not found',
      caseNotFoundHint: 'It may have been deleted; go back to the list of cases',
      loadCaseFailed: 'The test case could not be loaded',
      caseUpdated: 'Case updated',
      caseUpdateFailed: 'The case could not be updated',
      saving: 'Saving…',
      fieldsSaved: 'Fields saved',
      fieldsSaveFailed: 'The fields could not be saved',
      baselineCaptured: 'Baseline captured: review and correct the values before validating',
      baselineFailed: 'The baseline could not be captured',
      caseValidated: 'Case marked as validated',
      validateFailed: 'The case could not be validated',
      imageUploaded: 'Image uploaded',
      imageUploadFailed: 'The image could not be uploaded',
      uploadFront: 'Upload front',
      uploadBack: 'Upload back',
      deleteImage: 'Delete image',
      runTest: 'Run test',
      runningTest: 'Running…',
      runNeedsValidation: 'Validate the case before running it',
      testPassed: 'Test passed',
      testFailed: 'Test failed: review the fields',
      testError: 'Error during the run',
      noResultForCase: 'No result was found for this case',
      runTestFailed: 'The test could not be run',
      expectedFields: 'Expected fields',
      captureBaseline: 'Capture baseline',
      capturing: 'Capturing…',
      validate: 'Validate',
      saveFields: 'Save fields',
      baselineWarning: 'Fields captured from the OCR: review and correct the values before validating.',
      fieldName: 'Field name',
      expectedValue: 'Expected value',
      noFields: 'No expected fields',
      noFieldsHint: 'Use "Capture baseline" or add fields by hand',
      addField: 'Add field',
      removeField: 'Remove field',
      lastResult: 'Result: %{status}',
      fieldsMatched: '%{matched}/%{total} fields correct',
      fixFields: 'Correct the fields with the values read',
      fieldsFromResults: 'Fields updated with the values read: review and save',
      operationTypes: { 1: 'KYC single side', 2: 'KYC both sides', 3: 'MRZ single side', 4: 'MRZ both sides', 5: 'QR MiDNI', 6: 'Face comparison' },
      matchModes: { 1: 'Exact', 2: 'Contains', 3: 'Regular expression', 4: 'Not empty' },
      resultStatuses: { 1: 'Passed', 2: 'Failed', 3: 'Error' },
    },
    testPage: {
      subtitle: 'Send a document or a face to the service and see exactly what it returns',
      tool: 'Tool',
      helpLabel: 'Help',
      accountSection: 'Account that sends',
      howToSend: 'How it is sent',
      captureMode: 'Capture mode',
      transport: 'Transport',
      transportBase64: 'Base64',
      transportS3: 'S3',
      transportFile: 'File',
      whatToReturn: 'What is returned',
      dataMode: 'Data mode',
      dataModeHint: 'Raw data needs an account with diagnostic output',
      raw: 'Raw',
      compare: 'Mapping vs raw',
      debug: 'Debug',
      healthCheck: 'Check the service',
      healthFailed: 'The service check failed',
      healthLine: '%{role}: %{name} — %{state}',
      primary: 'Primary',
      secondary: 'Secondary',
      healthy: 'Healthy',
      down: 'Down',
      invalidFile: 'Only images and PDF files are allowed',
      unknownError: 'Unknown error',
      removeImage: 'Remove',
      switchCamera: 'Switch camera',
      closeCamera: 'Close the camera',
      cameraDenied: 'The browser is not allowed to use the camera. Allow camera access for this site and try again.',
      cameraNotFound: 'No camera was found on this device.',
      cameraBusy: 'The camera is in use by another application. Close it and try again.',
      cameraUnavailable: 'This browser does not allow the camera on this page.',
      cameraFailed: 'The camera could not be opened.',
      retryCamera: 'Try again',
      result: 'Result',
      resultViews: 'Result views',
      tabCount: '%{label} (%{count})',
      json: 'JSON',
      showImage: 'Show image',
      hideImage: 'Hide image',
      resizeImagePanel: 'Drag to resize the image panel',
      compareSummary: 'Summary',
      compareDetailMapped: 'Mapping detail',
      compareDetailRaw: 'Raw detail',
      facePhoto: 'Face photo from the document',
      timeOcr: 'OCR',
      timeGateway: 'Gateway',
      timeTotal: 'Total',
      fieldsRead: 'Fields read',
      residence: 'Residence',
      noResidence: 'This type of document carries no residence data',
      mapTitle: 'Map of the residence',
      postalCode: 'Postal code %{code}',
      mappings: 'Field mappings',
      mappingFields: '%{smart_count} field |||| %{smart_count} fields',
      showMappings: 'Show the mappings',
      hideMappings: 'Hide the mappings',
      noDocumentType: 'The document type could not be identified',
      noMappings: 'No custom mappings are configured for this document type',
      score: 'Score',
      yes: 'Yes',
      no: 'No',
      qrTooLarge: 'The vector is too large for a QR code',
      qrCode: 'QR code',
      qrAlt: 'QR code of the face embedding',
      faceMatchDistance: 'Face match distance: %{value}',
      notAvailable: 'not available',
      compareMode: 'What is compared',
      responseJson: 'Response JSON',
      gender: 'Gender',
      boundingBox: 'Face position',
      vectorFloats: '%{label} (%{count} floats)',
      caseCreated: 'Test case created and validated',
      createCaseHint: 'A test case is created with the current images and the fields read as expected values. The case is marked as validated.',
      caseName: 'Case name',
      caseDescription: 'Description (optional)',
      fieldCount: '%{smart_count} field |||| %{smart_count} fields',
      help: {
        title: 'Document test: how it works',
        intro: 'Test the OCR engine by uploading a document image and seeing the extracted data at once. Select an account, choose a capture mode and select Process.',
        modesTitle: 'Capture modes',
        single: 'Upload one image of one side of the document (e.g. the front of an ID card or the data page of a passport).',
        dualCombined: 'Upload the front and the back: they are combined into one image before they are sent to the engine.',
        dualSeparate: 'Upload the front and the back: each side is sent to the engine on its own, and the results are merged keeping the best score per field.',
        midni: 'For the Spanish DNI with a QR code (MiDNI). Enables QR extraction and the check of its digital signature.',
        optionsTitle: 'Options',
        face: 'Extract the cropped photograph of the face from the document.',
        signature: 'Extract the image of the signature, if there is one.',
        faceVector: 'Compute a 512-float biometric embedding of the face, for identity matching. Needs Face.',
        mapping: 'Apply the field mappings: raw field names become standard names. Adds a second call.',
        debug: 'Include the step-by-step mapping trace in the response.',
        orientation: 'The rotation of the document image is always detected and corrected.',
        timesTitle: 'Times',
        timeOcr: 'Time the OCR engine spent reading the document: the real cost of the extraction. If it is high, the bottleneck is the OCR server.',
        timeGateway: 'Time of the service in front of the OCR engine: authentication, routing to the OCR server and building the response. The difference from the OCR time is its overhead.',
        timeTotal: 'Time measured from the browser: the network, the administration service, the processing and the way back. The difference from the gateway time is network latency and administration overhead.',
      },
    },
  },
  ocr_testing: { tabs: { cases: 'Test cases', runs: 'Runs' } },
  users: {
    actions: {
      impersonate: 'Impersonate user',
    },
  },
  auth: {
    session_expired: 'Session expired',
  },
  statistics: {
    last_day: 'Last day',
    last_week: 'Last week',
    last_month: 'Last month',
    account: 'Account',
    last_access: 'Last access',
    total: 'Total',
    errors_col: 'Errors',
    health: 'Health',
    prefix: 'Prefix',
    prefix_all: 'All',
    no_results: 'No statistics found',
    rows_per_page: 'Rows per page:',
  },
  common: {
    filters: { label: 'Filters', all: 'All' },
    create: 'Create',
    more: '(+%{count} more)',
    import: 'Import',
    importDialog: {
      title: 'Import mappings',
      records: 'The file holds %{smart_count} record |||| The file holds %{smart_count} records',
      replace: 'Replace every existing mapping',
      replaceHint: 'When ticked, every current record is deleted and replaced by those in the file.',
      replaceWarning: 'Replace: every current mapping is deleted before importing. This cannot be undone.',
      mergeInfo: 'Merge: existing records are updated, new ones are added and the rest stay as they are.',
      resultTitle: 'Import result',
      total: 'Processed',
      created: 'Created',
      updated: 'Updated',
      skipped: 'Unchanged',
      deleted: 'Deleted',
      errors: '%{smart_count} error during the import |||| %{smart_count} errors during the import',
      errorRow: 'Row %{row}: %{message}',
      importing: 'Importing…',
      run: 'Import',
      runReplace: 'Replace and import',
      invalidFile: 'The file could not be read',
      emptyFile: 'The file is empty or its format is not recognised',
      summary: 'Import finished: %{created} created, %{updated} updated, %{skipped} unchanged, %{deleted} deleted, %{errors} errors',
      failed: 'The import could not be completed',
    },
  },
  'default-mapping': {
    source: 'Source',
    help_title: 'Default Mappings — How it works',
    help_intro: 'Default mappings define which KYC field is used to extract a standard piece of data (e.g. "surname", "documentNumber"). When multiple KYC fields can provide the same data, the priority determines which one is used first (0 = highest priority).',
    help_naming_title: 'KYC Field naming convention',
    help_mrz: 'Data read directly from the Machine Readable Zone (the coded lines at the bottom of the document)',
    help_viz: 'Data read from the Visual Inspection Zone (printed text on the document face)',
    help_norm: 'Normalized data — processed and standardized from multiple sources',
    help_qr: 'Data extracted from a QR or barcode on the document',
    help_example: 'Example: NORM_FRONT_MRZ_Surname = Surname extracted from the MRZ of the front side, after normalization. Priority 0 means this source is tried first.',
  },
  'fields-mapping': {
    help_title: 'Field Mappings — How it works',
    help_intro: 'Field mappings define how the OCR engine extracts data from a specific document type and nationality. Each mapping links standard field names (e.g. "DocumentNumber", "Surname") to internal KYC fields, with optional regex transformations.',
    help_columns_title: 'Columns explained',
    help_doc: 'Document',
    help_doc_desc: 'The document type (e.g. "Spain - Id Card (2021)") and the nationality code (e.g. "ESP"). Together they uniquely identify which document model to apply.',
    help_side: 'Side',
    help_front: '= only the front side of the document',
    help_back: '= only the back side',
    help_both: '= applies to both sides (e.g. combined image)',
    help_fields: 'Standard Fields',
    help_fields_desc: 'The list of standard field names (e.g. "DocumentNumber", "Surname") that this mapping extracts. Each field maps to one or more internal KYC fields with optional prefix/suffix and regex.',
    help_tip: 'Tip: Use the "Side" and "Standard field" filters to quickly find which documents extract a specific field.',
    both: {
      auto_managed_banner_title: 'Auto-generated row',
      auto_managed_banner: 'This row is generated automatically. To change it, edit the rule on the front or back side.',
      badge_label: 'Auto-generated',
      badge_tooltip: 'This row is composed from the front and back sides.',
      side_front: 'Front',
      side_back: 'Back',
      open_front: 'Open front side',
      open_back: 'Open back side',
      see_source: 'See source',
      empty_rules: 'No combined rules at the moment.',
      create_helper: 'The "Both sides" row is generated automatically.',
      save_success: 'Changes saved. The "Both sides" row has been updated automatically.',
      priority_dialog: {
        title: 'Priority resolution',
        subtitle: 'The following fields are defined on both sides. Choose the order in which they should apply.',
        confirm: 'Confirm and save',
        cancel: 'Cancel',
        move_up: 'Move up',
        move_down: 'Move down',
        regex_label: 'Regex',
        sources_label: 'Sources',
        conflict_hint: 'defined on front and back',
      },
      preview: {
        title: 'Combined row preview',
        loading: 'Calculating…',
        empty: 'No projected rules for the combined row.',
        conflicts_hint: 'Some fields are also defined on the other side. You will be asked to set the order when saving.',
      },
    },
  },
  accounts: {
    api_key_copied: 'API Key copied to clipboard',
    copy_api_key: 'Copy',
    change_api_key: 'Change API Key',
    api_key_warning: 'Changing the API key will immediately invalidate the current key. All integrations using the old key will stop working.',
    current_key: 'Current key',
    new_key: 'New key',
    generate_new: 'Generate another key',
    apply_new_key: 'Apply new key',
    api_key_changed: 'API Key updated successfully. Copy the new key now.',
    no_api_key: 'No API Key available',
    filter_primary_server: 'Primary server',
    filter_secondary_server: 'Secondary server',
    bulk_server: {
      button: 'Reassign server',
      title: 'Bulk reassign server',
      scope: 'Scope',
      scope_selected: 'Selected (%{n})',
      scope_filtered: 'All matching the filter (%{n})',
      need_scope: 'Select accounts or apply a filter to continue.',
      field: 'Field to change',
      new_server: 'New server',
      calculating: 'Calculating…',
      preview: 'Will change %{u} accounts. %{s} will be skipped (they would have the same server as primary and secondary).',
      preview_none: 'No accounts match the current selection.',
      apply: 'Apply',
      result_title: 'Result',
      r_matched: 'Matched: %{n}',
      r_updated: 'Updated: %{n}',
      r_skipped: 'Skipped (same server in both slots): %{n}',
      success: 'Servers updated successfully.',
    },
  },
  test: {
    account: 'Account',
    single_side: '1 side',
    dual_combined: '2 sides (combined)',
    dual_separate: '2 sides (separate)',
    midni: 'MiDNI',
    face_vector: 'Face vector',
    face: 'Face',
    use_camera: 'Use camera',
    mapping: 'Mapping',
    orientation: 'Auto-rotate',
    all_fields: 'All Fields',
    no_data: 'No data available',
    process: 'Process',
    processing: 'Processing...',
    clear: 'Clear',
    details: 'Details',
    front_image: 'Front image',
    back_image: 'Back image',
    drop_image: 'Drop image here or click to select',
    select_account_image: 'Select an account and upload an image',
    select_account: 'Select an account first',
    unknown: 'Unknown',
    scan_again: 'Scan Again',
    new_document: 'New Document',
    create_test_case: 'Create test case',
    mapped_data: 'Mapped Data',
    no_mapping_data: 'No mapped data available. The gateway mapping requires a real KYC engine (not mock) with field mappings configured.',
    no_face_signature: 'No face or signature extracted. The mock engine does not generate images.',
    mapping_debug: 'Mapping Debug',
    face_signature: 'Face & Signature',
    field: 'Field',
    value: 'Value',
    source: 'Source',
    reason: 'Reason',
    valid: 'Valid',
    language: 'Language',
    signature: 'Signature',
    section_ocr: 'Document OCR',
    section_face: 'Face Recognition',
    tab_help: {
      all_fields: 'All data extracted from the document in one flat list, grouped by source (MRZ, VIZ, NORM). Each field shows its name, value, and the OCR confidence score (0-100%). This is the raw engine output before any normalization.',
      mapped_data: 'Normalized data after applying field mappings. Standard field names (e.g. "FullName", "DocumentNumber") are mapped from the raw fields. This is the final data your integration receives. Only available if the "Mapping" option was enabled.',
      mrz: 'Machine Readable Zone — data decoded from the coded lines at the bottom of the document (the two or three lines of characters like P<FRABERNARD<<MARIE<<<). MRZ data is highly reliable as it follows an international standard (ICAO 9303).',
      viz: 'Visual Inspection Zone — data read from the printed text on the document face using OCR (optical character recognition). VIZ data can vary in accuracy depending on print quality, fonts, and document condition.',
      norm: 'Normalized data — the engine cross-references MRZ and VIZ data, resolves conflicts, and produces a cleaned, standardized result. For example, accented characters missing in MRZ are recovered from VIZ.',
      debug: 'Mapping trace showing how each raw field was transformed into a standard field. Shows the source field, target field, and any regex transformations applied. Useful for diagnosing mapping configuration issues.',
      face: 'Biometric images extracted from the document: the cropped facial photograph and the signature (if present). If "Face vector" was enabled, also shows the 512-float biometric embedding as a downloadable QR code for identity matching.',
      json: 'The complete raw JSON response from the API, exactly as returned by the KYC engine. Useful for developers integrating with the API or diagnosing unexpected responses.',
    },
    face_recognition: {
      compare_title: 'Face Comparison',
      mode_images: 'Two photos',
      mode_doc_selfie: 'Document + Selfie',
      document: 'Document',
      selfie: 'Selfie',
      image1: 'Image 1',
      image2: 'Image 2',
      image: 'Image',
      compare_btn: 'Compare',
      embed_title: 'Face Embedding Extraction',
      embed_btn: 'Extract Embedding',
      attributes_title: 'Age & Gender Estimation',
      attributes_desc: 'Lightweight endpoint — estimates age and gender without computing the face embedding. Ideal for age verification and access control.',
      estimate_btn: 'Estimate',
      verified: 'Verified — Same person',
      not_verified: 'Not verified — Different persons',
      similarity: 'Similarity',
      distance: 'Cosine distance',
      processing_time: 'Processing time',
      detection: 'Detection',
      age: 'Age',
      male: 'Male',
      female: 'Female',
      estimated_age: 'Estimated age',
      confidence: 'confidence',
      no_face_detected: 'No face detected in the image.',
      vector_label: 'Embedding vector',
      api_info: 'API Integration Info',
      request_body: 'Request body',
      response_example: 'Response example',
      auth_note: 'Authentication: Include header "Authorization: Bearer <token>" or "X-API-Key: <key>".',
      compare_api_desc: 'Compares two faces and returns a similarity score. Supports two input modes: two arbitrary photos (image1 + image2) or a document photo vs. a selfie (document + selfie). Requires authentication via API key or Bearer token.',
      embed_api_desc: 'Extracts a 512-dimensional face embedding from an image. The embedding can be stored and later compared using cosine distance, or sent to the /compare endpoint as a pre-computed vector.',
      attributes_api_desc: 'Estimates age and gender from a face image. No embedding is computed, making this faster than /embed. Supports single or batch processing.',
      download_qr: 'Download QR image',
      capture: 'Capture',
    },
  },
  dashboard: {
    heading: 'KYC Platform Dashboard',
    systemResources: 'System Resources',
    health: { healthy: 'Healthy', warning: 'Warning', critical: 'Critical' },
    kpi: {
      totalAccounts: 'Total Accounts',
      requestsToday: 'Requests Today',
      requestsThisWeek: 'requests this week',
      errorRate: 'Error Rate Today',
      mostActive: 'Most Active',
      serversConfigured: 'servers configured',
    },
    chart: {
      success: 'Success',
      errors: 'Errors',
      avgDaily: 'Daily average (7 days)',
      weekTotal: 'Week total',
      successRate: 'Success rate',
    },
    activity: {
      title: 'Account Activity',
      account: 'Account',
      health: 'Health',
      todaySuccessError: 'Today (Success / Error)',
      week: 'Week',
      month: 'Month',
      lastAccess: 'Last Access',
      never: 'Never',
    },
    resources: {
      accounts: 'Accounts',
      servers: 'Servers',
      licenses: 'Licenses',
      docTypes: 'Doc Types',
      nationalities: 'Nationalities',
      kycFields: 'KYC Fields',
      users: 'Users',
      mappings: 'Mappings',
      defaults: 'Defaults',
      costCenters: 'Cost Centers',
    },
    tabs: { panel: 'Panel', },
  },
  tech_support: {
    tabs: {
      overview: 'Overview',
      servers: 'KYC Servers',
      stress_test: 'Stress Test',
    },
    kpi: {
      gateway: 'API Gateway',
      kycServices: 'KYC Services',
      serversOnline: 'servers online',
      accounts: 'Accounts',
      errorRate: 'Error Rate',
    },
    gateway_health: {
      title: 'Gateway Health Status',
      unavailable: 'Detailed health status not available. Ensure at least one account is configured.',
      ocr_available: 'OCR available — document scanning is operational',
      ocr_unavailable: 'OCR unavailable — no OCR servers responding, document scanning disabled',
      ocr_servers: 'OCR Servers',
      no_ocr_servers: 'No OCR servers configured',
      local_capabilities: 'Local Capabilities',
      facematch_tooltip: 'Face comparison is processed locally in the gateway, does not depend on OCR servers',
      midni_tooltip: 'miDNI QR decoding is processed locally in the gateway, does not depend on OCR servers',
    },
    system_info: {
      title: 'System Information',
      version: 'Version',
      environment: 'Environment',
      gatewayUrl: 'Gateway URL',
    },
    health_checks: {
      title: 'Health Checks',
      service: 'Service',
      status: 'Status',
      latency: 'Latency',
    },
    servers: {
      empty: 'No servers registered',
      name: 'Name',
      url: 'URL',
      status: 'Status',
      latency: 'Latency',
      actions: 'Actions',
      ping: 'Measure response time',
      forcedOffline: 'Forced Offline',
    },
    stress: {
      disclaimer: 'All stress tests are executed by the Admin API server and routed through the API Gateway, never directly to KYC services. This provides realistic concurrency and latency measurements that reflect actual production request flows.',
      config_title: 'Test Configuration',
      scenarios: { gateway_health: 'Gateway Health Check', admin_api: 'Admin API Query', ocr: 'OCR Processing' },
      scenario: 'Scenario',
      account: 'Account',
      concurrent: 'Concurrent requests',
      duration: 'Duration',
      start: 'Start Test',
      cancel: 'Cancel',
      progress_title: 'Progress',
      completed: 'Completed',
      errors: 'Errors',
      avg_latency: 'Avg latency',
      results_title: 'Results',
      error_rate: 'Error rate',
      latency_distribution: 'Latency Distribution',
      total_requests: 'Total requests',
      successful: 'Successful',
      failed: 'Failed',
      error_breakdown: 'Error Breakdown',
      count: 'Count',
      message: 'Message',
      test_image: 'Test image (document)',
      upload_image: 'Select document image...',
      image_loaded: 'Image loaded',
      image_ready: 'Image ready — it will be sent with each request',
      placeholder: 'Configure and start a stress test to see results here',
      history_title: 'Test History',
      history_date: 'Date',
    },
  },
};

/* ─── Spanish (full override) ─── */
const es = {
  ...englishMessages,
  ra: {
    ...englishMessages.ra,
    action: {
      ...englishMessages.ra.action,
      add_filter: 'Agregar filtro',
      add: 'Agregar',
      back: 'Volver',
      bulk_actions: '1 elemento seleccionado |||| %{smart_count} elementos seleccionados',
      cancel: 'Cancelar',
      clear_array_input: 'Vaciar lista',
      clear_input_value: 'Limpiar campo',
      clone: 'Duplicar',
      confirm: 'Confirmar',
      create: 'Crear',
      create_item: 'Crear %{item}',
      delete: 'Eliminar',
      edit: 'Editar',
      export: 'Exportar',
      list: 'Lista',
      refresh: 'Actualizar',
      remove_filter: 'Quitar filtro',
      remove_all_filters: 'Quitar todos los filtros',
      remove: 'Eliminar',
      save: 'Guardar',
      search: 'Buscar',
      select_all: 'Seleccionar todo',
      select_row: 'Seleccionar fila',
      show: 'Ver',
      sort: 'Ordenar',
      undo: 'Deshacer',
      unselect: 'Deseleccionar',
      expand: 'Expandir',
      close: 'Cerrar',
      open_menu: 'Abrir menú',
      close_menu: 'Cerrar menú',
      update: 'Actualizar',
      move_up: 'Mover arriba',
      move_down: 'Mover abajo',
      open: 'Abrir',
      toggle_theme: 'Cambiar tema',
      select_columns: 'Columnas',
    },
    boolean: { ...englishMessages.ra.boolean, true: 'Sí', false: 'No', null: ' ' },
    page: {
      ...englishMessages.ra.page,
      create: 'Crear %{name}',
      dashboard: 'Panel',
      edit: '%{name} #%{id}',
      error: 'Se produjo un error',
      list: '%{name}',
      loading: 'Cargando',
      not_found: 'No encontrado',
      show: '%{name} #%{id}',
      empty: 'Sin %{name} todavía.',
      invite: '¿Desea agregar uno?',
    },
    input: {
      ...englishMessages.ra.input,
      file: { upload_several: 'Arrastre archivos aquí o haga clic para seleccionar.', upload_single: 'Arrastre un archivo aquí o haga clic para seleccionar.' },
      image: { upload_several: 'Arrastre imágenes aquí o haga clic para seleccionar.', upload_single: 'Arrastre una imagen aquí o haga clic para seleccionar.' },
      references: { all_missing: 'No se encontraron referencias.', many_missing: 'Algunas referencias no están disponibles.', single_missing: 'La referencia no está disponible.' },
      password: { toggle_visible: 'Mostrar contraseña', toggle_hidden: 'Ocultar contraseña' },
    },
    message: {
      ...englishMessages.ra.message,
      about: 'Acerca de',
      are_you_sure: '¿Está seguro?',
      auth_error: 'Error de autenticación',
      bulk_delete_content: '¿Está seguro de que desea eliminar %{name}? |||| ¿Está seguro de que desea eliminar estos %{smart_count} elementos?',
      bulk_delete_title: 'Eliminar %{name} |||| Eliminar %{smart_count} %{name}',
      bulk_update_content: '¿Está seguro de que desea actualizar %{name}? |||| ¿Está seguro de que desea actualizar estos %{smart_count} elementos?',
      bulk_update_title: 'Actualizar %{name} |||| Actualizar %{smart_count} %{name}',
      clear_array_input: '¿Está seguro de que desea vaciar la lista?',
      delete_content: '¿Está seguro de que desea eliminar este elemento?',
      delete_title: 'Eliminar %{name} #%{id}',
      details: 'Detalles',
      error: 'Se produjo un error en el cliente y su solicitud no se pudo completar.',
      invalid_form: 'El formulario no es válido. Compruebe los errores.',
      loading: 'La página se está cargando, espere un momento.',
      no: 'No',
      not_found: 'La URL introducida es incorrecta o el enlace está roto.',
      yes: 'Sí',
      unsaved_changes: 'Algunos cambios no se guardaron. ¿Desea descartarlos?',
    },
    navigation: {
      ...englishMessages.ra.navigation,
      no_results: 'No se encontraron resultados',
      no_more_results: 'La página %{page} está fuera del rango. Pruebe la página anterior.',
      page_out_of_boundaries: 'La página %{page} está fuera del rango',
      page_out_from_end: 'No puede ir después de la última página',
      page_out_from_begin: 'No puede ir antes de la página 1',
      page_range_info: '%{offsetBegin}-%{offsetEnd} de %{total}',
      partial_page_range_info: '%{offsetBegin}-%{offsetEnd} de más de %{offsetEnd}',
      current_page: 'Página %{page}',
      page: 'Ir a la página %{page}',
      first: 'Ir a la primera página',
      last: 'Ir a la última página',
      next: 'Siguiente',
      previous: 'Anterior',
      page_rows_per_page: 'Filas por página:',
      skip_nav: 'Ir al contenido',
    },
    sort: { ...englishMessages.ra.sort, sort_by: 'Ordenar por %{field} %{order}', ASC: 'ascendente', DESC: 'descendente' },
    auth: { ...englishMessages.ra.auth, auth_check_error: 'Por favor, inicie sesión para continuar', user_menu: 'Perfil', username: 'Usuario', password: 'Contraseña', sign_in: 'Iniciar sesión', sign_in_error: 'Error de autenticación, vuelva a intentarlo', logout: 'Cerrar sesión' },
    notification: {
      ...englishMessages.ra.notification,
      updated: 'Registro actualizado |||| %{smart_count} registros actualizados',
      created: 'Registro creado',
      deleted: 'Registro eliminado |||| %{smart_count} registros eliminados',
      bad_item: 'Elemento incorrecto',
      item_doesnt_exist: 'El elemento no existe',
      http_error: 'Error de comunicación con el servidor',
      data_provider_error: 'Error del proveedor de datos. Consulte la consola para más detalles.',
      i18n_error: 'No se pudieron cargar las traducciones para el idioma especificado',
      canceled: 'Acción cancelada',
      logged_out: 'Su sesión ha finalizado, vuelva a conectarse.',
      not_authorized: 'No tiene permisos para acceder a este recurso.',
    },
    validation: {
      ...englishMessages.ra.validation,
      required: 'Obligatorio',
      minLength: 'Debe tener al menos %{min} caracteres',
      maxLength: 'Debe tener %{max} caracteres o menos',
      minValue: 'Debe ser al menos %{min}',
      maxValue: 'Debe ser %{max} o menos',
      number: 'Debe ser un número',
      email: 'Debe ser un correo electrónico válido',
      oneOf: 'Debe ser uno de: %{options}',
      regex: 'Debe coincidir con el formato específico (regexp): %{pattern}',
    },
    saved_queries: {
      ...englishMessages.ra.saved_queries,
      label: 'Consultas guardadas',
      query_name: 'Nombre de la consulta',
      new_label: 'Guardar consulta actual...',
      new_dialog_title: 'Guardar consulta actual como',
      remove_label: 'Eliminar consulta guardada',
      remove_label_with_name: 'Eliminar consulta "%{name}"',
      remove_dialog_title: '¿Eliminar consulta guardada?',
      remove_message: '¿Está seguro de que desea eliminar esta consulta de la lista de consultas guardadas?',
      help: 'Filtre la lista y guarde esta consulta para más tarde',
    },
    configurable: {
      ...englishMessages.ra.configurable,
      customize: 'Personalizar',
      configureMode: 'Configurar esta página',
      inspector: {
        ...(englishMessages.ra.configurable as any)?.inspector,
        title: 'Inspector',
        content: 'Pase el cursor sobre los elementos de la interfaz para configurarlos',
        reset: 'Restablecer configuración',
        hideAll: 'Ocultar todo',
        showAll: 'Mostrar todo',
      },
    },
  },
  resources: {
    accounts: { name: 'Cuenta |||| Cuentas', fields: { 'accountResponse.accountName': 'Nombre de cuenta', 'accountResponse.accountStatus': 'Estado', 'licenseResponse.licenseName': 'Licencia', validityTimeFrame: 'Validez', isLicenseBlocked: 'Bloqueada', 'accountResponse.rateLimitApplies': 'Límite', 'accountResponse.loggingStatus': 'Log', primaryServer: 'Servidor principal', secondaryServer: 'Servidor secundario', 'accountResponse.allowDebugOutput': 'Salida de diagnóstico', 'accountResponse.allowDebugOutputHelp': 'Permite que esta cuenta reciba campos de diagnóstico interno en las respuestas de la API (solo si los solicita explícitamente).' } },
    servers: { name: 'Servidor |||| Servidores', fields: { name: 'Nombre', port: 'Puerto', url: 'URL del servidor', status: 'Estado', isForcedOffline: 'Forzar desconexión', apiKey: 'Clave API' } },
    licenses: { name: 'Licencia |||| Licencias', fields: { licenseName: 'Nombre de licencia', servers: 'Servidores' } },
    'document-types': { name: 'Tipo de documento |||| Tipos de documento', fields: { name: 'Nombre', mappingsCount: 'Extracción OCR', createdAt: 'Creado', configured: 'reglas', notConfigured: 'Sin configurar', mappingsTooltipYes: 'Este tipo de documento tiene %{count} reglas que definen qué campos extrae el motor OCR (ej: nombre, número de documento, fecha de caducidad)', mappingsTooltipNo: 'No hay reglas de extracción definidas — el motor OCR no sabrá qué campos extraer de este tipo de documento' } },
    nationalities: { name: 'Nacionalidad |||| Nacionalidades', fields: { name: 'Nombre', mappingsCount: 'Extracción OCR', createdAt: 'Creado', configured: 'reglas', notConfigured: 'Sin configurar', mappingsTooltipYes: 'Esta nacionalidad tiene %{count} reglas de extracción que definen cómo el motor OCR procesa documentos de este país', mappingsTooltipNo: 'No hay reglas de extracción definidas para esta nacionalidad' } },
    'kyc-fields': { name: 'Campo KYC |||| Campos KYC', fields: { name: 'Nombre', usageCount: 'Uso', createdAt: 'Creado', usedIn: 'Usado en %{count} mapeos', unused: 'Sin uso', usageTooltipYes: 'Este campo KYC se referencia en %{count} mapeos por defecto', usageTooltipNo: 'Este campo KYC no se utiliza en ningún mapeo por defecto' } },
    users: { name: 'Usuario |||| Usuarios', fields: { name: 'Nombre', email: 'Correo electrónico', role: 'Rol', isExternalUser: 'Usuario externo', password: 'Contraseña' } },
    'fields-mapping': { name: 'Mapeo de campos |||| Mapeos de campos', fields: { document: 'Documento', side: 'Cara', standardFields: 'Campos estándar', fieldsCount: 'Campos', standardField: 'Campo estándar' } },
    'default-mapping': { name: 'Mapeo por defecto |||| Mapeos por defecto', fields: { standardFieldName: 'Campo estándar', 'kycFields.name': 'Campo KYC', priority: 'Prioridad' } },
    statistics: { name: 'Estadísticas' },
    'cost-centers': { name: 'Centro de coste |||| Centros de coste', fields: { name: 'Nombre', description: 'Descripción', unpaidCreditLimit: 'Límite de crédito impagado', isCreditsBlocked: 'Créditos bloqueados' } },
  },
  menu: {
    kyc_config: 'Configuración KYC',
    billing: 'Facturación',
    system: 'Sistema',
    backups: 'Copias de seguridad',
    credits: 'Créditos',
    pricing: 'Tarifas',
    photo_upload: 'Test',
    ocr_testing: 'Pruebas OCR',
    developers: 'Desarrolladores',
  },
  shell: {
    // The environment pill is the one uppercase label of the direction and reads
    // the same in every language (hybrid-3c §5).
    environment: { nonProduction: 'TEST ENV', local: 'LOCAL' },
    impersonation: {
      active: 'Suplantando a %{name}',
      exit: 'Salir',
    },
    breadcrumb: { label: 'Ruta de navegación', detail: 'Detalle' },
    rail: { label: 'Navegación principal' },
    profile: { menu: 'Menú de la cuenta', language: 'Idioma', session: 'Sesión: %{time}' },
    empty: { title: 'No se han encontrado resultados', hint: 'Modifique la búsqueda o los filtros' },
    sectionHeader: { more: 'Más acciones', moreCreate: 'Otras formas de crear' },
    rowActions: { more: 'Acciones' },
    code: { copy: 'Copiar', copied: 'Copiado al portapapeles' },
    form: { create: 'Crear %{name}', edit: 'Editar %{name}' },
    exportDialog: { title: 'Exportar', json: 'JSON: el archivo que lee Importar', csv: 'CSV: para una hoja de cálculo' },
  },
  roles: { Administrator: 'Administrador', AdvancedUser: 'Usuario avanzado', User: 'Usuario', NoAccess: 'Sin acceso', Developer: 'Desarrollador' },
  developers: {
    tabs: {
      overview: 'Primeros pasos',
      reference: 'Referencia de la API',
      recipes: 'Ejemplos de código',
      downloads: 'Postman y OpenAPI',
    },
    operations: '%{smart_count} operación |||| %{smart_count} operaciones',
    quickStart: 'Inicio rápido',
    environments: 'Entornos',
    thisPanel: 'Este panel',
    environmentsNote: 'Desarrolle y pruebe su integración contra el entorno de pruebas antes de pasar a producción.',
    assistant: {
      title: 'Trabajar con un asistente de IA',
      text: 'Descargue el fichero OpenAPI y entrégueselo a su asistente de programación o a un generador de clientes: es el contrato exacto de la versión desplegada ahora mismo, con un ejemplo para cada petición.',
      action: 'Ir a las descargas',
    },
    loading: 'Cargando…',
    loadingReference: 'Cargando la referencia…',
    samplesIntro: 'Los ejemplos apuntan a `%{url}`, el entorno con el que trabaja este panel, y leen su clave de API de la variable de entorno `%{variable}`: defínala antes de ejecutarlos. Las imágenes se leen de ficheros situados junto al ejemplo.',
    language: 'Lenguaje',
    postman: {
      title: 'Postman',
      text: 'Cada operación de la referencia como una petición lista para enviar, con el cuerpo relleno y las credenciales en variables.',
      collection: 'Descargar la colección',
      step1: 'Importe en Postman la colección y el fichero de un entorno.',
      step2: 'Seleccione el entorno y asigne %{variables}.',
      step3: 'Abra %{request}, ponga una imagen suya en el cuerpo y pulse Send.',
    },
    openapi: {
      title: 'OpenAPI',
      text: 'La especificación OpenAPI 3.1 de la versión desplegada ahora mismo%{version}. Genere con ella un cliente tipado, impórtela en Insomnia, Bruno o Hoppscotch, o entregue a un asistente de programación el contrato exacto.',
      download: 'Descargar el fichero OpenAPI',
    },
    errors: {
      forbidden: 'Su usuario no tiene acceso a la documentación de la API de KYC. Solicítelo a su contacto en Roommatik.',
      unavailable: 'La especificación de la API no está disponible en este momento (%{status}). Los primeros pasos y los ejemplos de código no dependen de ella.',
      viewer: 'No se ha podido cargar la referencia de la API. Recargue la página para intentarlo de nuevo.',
      download: 'No se ha podido completar la descarga (%{status}). Inténtelo de nuevo en unos instantes.',
    },
    noAccess: {
      pageTitle: 'Sin acceso',
      title: 'Su usuario no tiene acceso a este panel',
      hint: 'Solicite acceso al panel o a la documentación de la API al administrador de su cuenta de KYC o a su contacto en Roommatik.',
    },
  },
  // Textos por pantalla introducidos por el rediseño 3c Hybrid: una clave por pantalla.
  screens: {
    pricing: {
      subtitle: 'Créditos que consume cada operación para un centro de coste',
      pickHint: 'Aquí aparecen las tarifas de sus operaciones',
      help: 'Ayuda',
      helpTitle: 'Tarifas — cómo funcionan',
      helpIntro: 'Las tarifas definen cuántos créditos consume cada tipo de operación para un centro de coste. Si no se configuran, se aplican los valores por defecto.',
      helpDefaults: 'Los valores por defecto se muestran como sugerencia. Puede configurar una tarifa diferente para cada centro de coste.',
      operationTypes: 'Tipos de operación',
      operations: {
        KycSingleSide: { name: 'KYC, una cara', description: 'Validación KYC de una sola cara del documento' },
        KycDualSide: { name: 'KYC, dos caras', description: 'Validación KYC de ambas caras del documento' },
        MrzSingleSide: { name: 'MRZ, una cara', description: 'Lectura MRZ de una sola cara' },
        MrzDualSide: { name: 'MRZ, dos caras', description: 'Lectura MRZ de ambas caras' },
      },
      credits: '%{count} crédito |||| %{count} créditos',
      byOperation: 'Tarifas por operación',
      tableScope: 'Una operación sin tarifa propia usa el valor por defecto',
      operationType: 'Tipo de operación',
      creditsPerOperation: 'Créditos por operación',
      creditsFor: 'Créditos por operación de %{name}',
      state: 'Tarifa',
      custom: 'Personalizada',
      default: 'Por defecto',
      defaultValue: 'Por defecto: %{count} crédito |||| Por defecto: %{count} créditos',
      defaultShort: '%{count} (por defecto)',
      edit: 'Editar tarifa',
      restore: 'Restaurar valor por defecto',
      restoreTitle: '¿Restaurar la tarifa por defecto?',
      restoreBody: '%{name} volverá a consumir el valor por defecto de %{count} crédito para este centro de coste. |||| %{name} volverá a consumir el valor por defecto de %{count} créditos para este centro de coste.',
      restoreConfirm: 'Restaurar',
      saved: 'Tarifa guardada correctamente',
      saveFailed: 'No se ha podido guardar la tarifa',
      restored: 'Tarifa eliminada; se aplica el valor por defecto',
      restoreFailed: 'No se ha podido eliminar la tarifa',
    },
    billing: {
      costCenter: 'Centro de coste',
      pickCostCenter: 'Seleccione un centro de coste',
    },
    credits: {
      subtitle: 'Saldo, movimientos y consumo de un centro de coste',
      pickHint: 'Aquí aparecen su saldo, sus transacciones y su resumen mensual',
      help: 'Ayuda',
      helpTitle: 'Créditos — cómo funcionan',
      helpIntro: 'Cada centro de coste tiene un saldo de créditos que se consume automáticamente cuando sus cuentas realizan operaciones KYC. El coste de cada operación depende de las tarifas del centro.',
      helpAvailableTitle: 'Créditos disponibles',
      helpAvailable: 'Saldo actual listo para consumir.',
      helpReservedTitle: 'Créditos reservados',
      helpReserved: 'Créditos retenidos temporalmente por operaciones en curso.',
      helpTopUpTitle: 'Recarga',
      helpTopUp: 'Añade créditos al saldo del centro. Se registra como una transacción de recarga.',
      helpAdjustmentTitle: 'Ajuste manual',
      helpAdjustment: 'Permite sumar o restar créditos con un motivo. Los valores negativos restan del saldo.',
      topUp: 'Recargar créditos',
      adjust: 'Ajuste manual',
      available: 'Créditos disponibles',
      reserved: 'Créditos reservados',
      status: 'Estado',
      active: 'Activo',
      blocked: 'Bloqueado',
      blockedDetail: 'Sus cuentas no pueden consumir créditos',
      transactions: 'Historial de transacciones',
      noTransactions: 'No hay transacciones registradas',
      noTransactionsHint: 'Aquí aparecen las recargas, reembolsos, consumos y ajustes',
      date: 'Fecha',
      type: 'Tipo',
      amount: 'Cantidad',
      balanceAfter: 'Saldo después',
      types: { TOPUP: 'Recarga', REFUND: 'Reembolso', DEDUCT: 'Consumo', MANUAL_ADJ: 'Ajuste manual' },
      monthly: 'Resumen mensual',
      previousYear: 'Año anterior',
      nextYear: 'Año siguiente',
      noMonthly: 'Sin datos para %{year}',
      noMonthlyHint: 'Cambie el año con las flechas',
      month: 'Mes',
      operations: 'Operaciones',
      creditsConsumed: 'Créditos consumidos',
      events: 'Registro de eventos',
      noEvents: 'No hay eventos registrados',
      noEventsHint: 'Aquí aparece cada operación que consume créditos',
      operationType: 'Tipo de operación',
      topUpTitle: 'Recargar créditos — %{name}',
      topUpHint: 'Introduzca la cantidad de créditos que desea añadir al saldo del centro de coste.',
      topUpConfirm: 'Recargar',
      topUpDone: 'Recarga realizada correctamente',
      topUpFailed: 'No se ha podido realizar la recarga',
      adjustTitle: 'Ajuste manual — %{name}',
      adjustHint: 'Los valores positivos suman créditos y los negativos los restan. Debe indicar un motivo.',
      adjustConfirm: 'Aplicar ajuste',
      adjustmentDone: 'Ajuste aplicado correctamente',
      adjustmentFailed: 'No se ha podido aplicar el ajuste',
      quantity: 'Cantidad',
      reason: 'Motivo',
      reasonHelp: 'Obligatorio. Explique el motivo del ajuste.',
    },
    statistics: {
      subtitle: 'Solicitudes correctas y fallidas de cada cuenta en el periodo',
      period: 'Periodo',
      periodColumn: 'Correctas / fallidas (%{period})',
      okErr: '%{ok} / %{err}',
      successful: 'Correctas',
      errorRate: 'Tasa de error',
      api: 'API',
      sortBy: 'Ordenar por',
      sortAsc: '%{field}, ascendente',
      sortDesc: '%{field}, descendente',
      resultCount: '%{count} cuenta |||| %{count} cuentas',
      prefixKiosk: 'KR — Kioscos',
      prefixOnline: 'OC — Check-in online',
      loadFailed: 'No se han podido cargar las estadísticas',
      loadFailedHint: 'Actualice para volver a intentarlo',
    },
    stressTest: {
      subtitle: 'Peticiones concurrentes a través del gateway, con la latencia y los errores que producen',
      startFailed: 'No se ha podido ejecutar el test: %{error}',
      rps: 'Peticiones/s',
      avg: 'Latencia media',
      rateBase: '%{failed} de %{total} peticiones',
      minMax: 'Latencia mín. / máx.',
      statusCode: 'Código HTTP',
      network: 'Red',
      bucketTooltip: '%{bucket} ms: %{count} peticiones',
      bucketAxis: 'Latencia (ms)',
      placeholderHint: 'Los resultados aparecen aquí cuando termina el test',
      historyCount: '%{smart_count} test |||| %{smart_count} tests',
      showHistory: 'Mostrar',
      hideHistory: 'Ocultar',
    },
    techSupport: {
      overviewSubtitle: 'Gateway, servidores KYC y tasa de error de los últimos 7 días',
      loadFailed: 'No se ha podido cargar la información del sistema. Actualice para volver a intentarlo.',
      health: { healthy: 'Correcto', degraded: 'Degradado', down: 'Caído' },
      online: 'En línea',
      offline: 'Sin conexión',
      accountsDetail: 'registradas',
      lastSevenDays: 'últimos 7 días',
      noRequestsWeek: 'Sin solicitudes en los últimos 7 días',
      primary: 'Principal',
      secondary: 'Secundario',
      ocrCheck: 'Servidor OCR %{role} — %{name}',
      faceMatch: 'Comparación facial',
      miDniQr: 'QR del miDNI',
      available: 'Disponible',
      unavailable: 'No disponible',
      unknown: 'Sin datos',
      servicesSubtitle: 'Lo que informa el gateway y el tiempo de respuesta de cada servidor registrado',
      registeredServers: 'Servidores registrados',
      registeredCount: '%{smart_count} servidor |||| %{smart_count} servidores',
      port: 'Puerto %{port}',
      notMeasured: 'Sin medir',
      unreachable: 'No responde',
      serversEmptyHint: 'Los servidores se añaden en Sistema › Servidores',
    },
    dashboard: {
      subtitle: 'Solicitudes de cada cuenta y recursos configurados',
      requestsDetail: '%{ok} correctas · %{err} fallidas',
      rateBase: '%{err} de %{total} solicitudes fallidas',
      noRequestsToday: 'Todavía no hay solicitudes hoy',
      requestsTodayLower: 'solicitudes hoy',
      noRecentActivity: 'Sin actividad en los últimos 7 días',
      weekByAccount: 'Solicitudes por cuenta',
      weekByAccountScope: 'Últimos 7 días · las %{count} cuentas con más actividad',
      barLabel: '%{total} (%{err} fallidas)',
      barAria: '%{name}: %{ok} correctas, %{err} fallidas',
      noWeekRequests: 'Sin solicitudes en los últimos 7 días',
      noWeekRequestsHint: 'Las cuentas aparecen aquí tras su primera solicitud',
      resourcesScope: 'Registros configurados en cada sección',
      activityScope: 'Todas las cuentas, primero las de más actividad hoy',
      today: 'Hoy',
      okErr: '%{ok} / %{err}',
      noActivity: 'Ninguna cuenta tiene actividad todavía',
      noActivityHint: 'Las cuentas aparecen aquí tras su primera solicitud',
      statsUnavailable: 'No se han podido cargar las estadísticas',
      statsUnavailableHint: 'Actualice para volver a intentarlo',
    },
    documentTypes: {
      subtitle: 'Documentos que reconoce el motor OCR y qué campos lee de cada uno',
      usesDefault: 'Usa la configuración por defecto',
      more: '(+%{count} más)',
    import: 'Importar',
    importDialog: {
      title: 'Importar mapeos',
      records: 'El archivo contiene %{smart_count} registro |||| El archivo contiene %{smart_count} registros',
      replace: 'Reemplazar todos los mapeos existentes',
      replaceHint: 'Si está marcado, se eliminan todos los registros actuales y se sustituyen por los del archivo.',
      replaceWarning: 'Reemplazar: se eliminarán todos los mapeos actuales antes de importar. Esta operación no se puede deshacer.',
      mergeInfo: 'Combinar: los registros existentes se actualizan, los nuevos se añaden y el resto se mantiene sin cambios.',
      resultTitle: 'Resultado de la importación',
      total: 'Procesados',
      created: 'Creados',
      updated: 'Actualizados',
      skipped: 'Sin cambios',
      deleted: 'Eliminados',
      errors: '%{smart_count} error durante la importación |||| %{smart_count} errores durante la importación',
      errorRow: 'Fila %{row}: %{message}',
      importing: 'Importando…',
      run: 'Importar',
      runReplace: 'Reemplazar e importar',
      invalidFile: 'No se ha podido leer el archivo',
      emptyFile: 'El archivo está vacío o su formato no se reconoce',
      summary: 'Importación finalizada: %{created} creados, %{updated} actualizados, %{skipped} sin cambios, %{deleted} eliminados, %{errors} errores',
      failed: 'No se ha podido completar la importación',
    },
    },
    nationalities: {
      subtitle: 'Países cuyos documentos lee el motor OCR y qué tipos de documento están configurados para cada uno',
      documents: 'Documentos',
    },
    defaultMappings: {
      subtitle: 'Qué campo KYC rellena cada campo estándar y en qué orden se prueban',
      help: 'Cómo funciona',
      helpQr: 'Los campos QR (miDNI) se muestran aparte porque proceden exclusivamente del código QR del DNI electrónico español y no compiten con los campos leídos del documento. Siempre tienen prioridad 0.',
      priorityHelp: '0 = máxima prioridad. Si varios campos KYC rellenan el mismo campo estándar, se usa el de menor número.',
      priorityDecrease: 'Bajar el número (más prioridad)',
      priorityIncrease: 'Subir el número (menos prioridad)',
      prioritySave: 'Guardar prioridad',
      priorityUpdated: 'Prioridad actualizada',
      priorityFailed: 'No se ha podido actualizar la prioridad',
      qrTitle: 'Campos QR (miDNI) · %{count}',
      qrInfo: 'Estos campos proceden exclusivamente del código QR del DNI electrónico español (miDNI). No compiten con los campos leídos del documento, se devuelven siempre que el QR está presente y no requieren prioridad.',
      qrField: 'Campo QR',
      exported: 'Exportación completada (%{smart_count} registro) |||| Exportación completada (%{smart_count} registros)',
      exportFailed: 'No se ha podido completar la exportación',
      sources: {
        MRZ: 'MRZ — zona de lectura mecánica',
        NORM: 'NORM — datos normalizados',
        VIZ: 'VIZ — zona de inspección visual',
        QR: 'QR — datos del código QR',
      },
    },
    fieldMappings: {
      subtitle: 'Cómo convierte el motor OCR lo que lee de cada documento y cara en la respuesta estándar',
      help: 'Cómo funciona',
      exported: 'Exportación completada (%{smart_count} registro) |||| Exportación completada (%{smart_count} registros)',
      exportFailed: 'No se ha podido completar la exportación',
      documentInfo: 'Documento',
      documentType: 'Tipo de documento',
      nationality: 'Nacionalidad',
      documentSide: 'Cara del documento',
      mappingFields: 'Reglas de mapeo',
      pickNationalityFirst: 'Seleccione antes una nacionalidad',
      regexMark: 'Regex: %{regex}',
      sideBoth: 'Ambas caras',
      conflictCount: '%{smart_count} conflicto |||| %{smart_count} conflictos',
      editor: {
        ruleCount: '%{smart_count} regla |||| %{smart_count} reglas',
        showExample: 'Ver un ejemplo',
        addRule: 'Añadir regla',
        addRuleHint: 'Añadir una nueva regla de mapeo',
        completeRuleFirst: 'Complete la regla actual antes de añadir otra',
        duplicates: 'Hay nombres de campo de salida repetidos. Cada uno debe ser único.',
        noRules: 'Aún no hay reglas de mapeo. Pulse «Añadir regla» para definir cómo se traduce lo que lee el motor OCR a la respuesta normalizada.',
        ruleNumber: 'Regla %{n}',
        oneRuleRequired: 'Se requiere al menos una regla',
        removeRule: 'Eliminar esta regla',
        output: 'Salida',
        outputInfo: 'El campo de salida que aparece en la respuesta normalizada. Es el nombre estándar que reciben los consumidores del servicio, sea cual sea el documento escaneado.',
        normalisedField: 'Campo de la respuesta normalizada',
        normalisedFieldInfo: 'Nombre del campo en la respuesta normalizada, por ejemplo surname, documentNumber o dateOfBirth.',
        normalisedFieldPlaceholder: 'p. ej.: surname',
        duplicateName: 'Este nombre está repetido',
        regex: 'Transformación regex (opcional)',
        regexInfo: 'Expresión regular opcional que se aplica al valor extraído antes de devolverlo. Por ejemplo, ^[A-Z\\s]+ conserva solo mayúsculas y espacios, y filtra caracteres inesperados de un nombre.',
        regexPlaceholder: 'p. ej.: ^[A-Z\\s]+',
        regexTester: 'Probador de regex',
        regexTesterInfo: 'Pruebe la expresión regular en tiempo real: introduzca un valor de ejemplo tal como lo devolvería el motor OCR y compruebe qué resultado da la transformación.',
        regexTesterPlaceholder: 'Introduzca un valor de ejemplo para probar',
        regexResult: 'Resultado:',
        regexNoMatch: '(sin coincidencia)',
        regexInvalid: 'Regex no válida: %{message}',
        sources: 'Orígenes',
        sourcesInfo: 'De dónde sale el valor de este campo: un campo que el motor OCR extrae del documento (lo habitual) o un valor fijo que se devuelve siempre. Si hay varios orígenes, se prueban en orden y se usa el primero que tenga valor.',
        sourcesOrder: 'Se prueban en orden; gana el primero con valor',
        sourcePosition: 'Origen %{n}',
        sourceKind: 'Tipo de origen',
        sourceOcr: 'OCR',
        sourceFixed: 'Fijo',
        oneSourceRequired: 'Se requiere al menos un origen',
        removeSource: 'Eliminar origen',
        ocrField: 'Campo OCR',
        ocrFieldInfo: 'El campo del motor OCR del que se toma el valor, como MRZ_Surname o VIZ_DocumentNumber. Cada uno corresponde a una zona y a un dato concretos del documento.',
        ocrFieldPlaceholder: 'Seleccione un campo',
        prefix: 'Prefijo',
        prefixHelp: 'Opcional, se añade delante',
        suffix: 'Sufijo',
        suffixHelp: 'Opcional, se añade detrás',
        fixedValue: 'Valor fijo',
        fixedValueInfo: 'Texto literal que se devuelve siempre como valor de este campo, sin depender de lo que lea el motor OCR. Útil para valores constantes según el tipo de documento, como P para un pasaporte.',
        fixedValuePlaceholder: 'p. ej.: P, ID_CARD',
        addSource: 'Añadir origen de respaldo',
        addSourceHint: 'Añadir otro origen que se pruebe cuando los anteriores estén vacíos',
        completeSourcesFirst: 'Complete los orígenes existentes antes de añadir otro',
        helpTitle: 'Mapeo de campos: cómo funciona',
        helpIntro: 'Cada regla define cómo se construye un campo de salida de la respuesta a partir de los datos brutos que lee el motor OCR. El motor extrae valores con nombres internos como MRZ_Surname o VIZ_DocumentNumber; el mapeo los traduce a nombres limpios y estándar como surname o documentNumber.',
        exampleTitle: 'Un ejemplo completo',
        exampleOutput: 'el nombre del campo en la respuesta normalizada',
        exampleSources: 'De dónde se toma el valor, por orden de prioridad:',
        exampleMrz: 'zona de lectura mecánica',
        exampleViz: 'zona de inspección visual',
        exampleNorm: 'normalizado, anverso',
        exampleExplanation: 'Los orígenes se prueban en orden. Si MRZ_Surname viene vacío (MRZ dañada), se prueba VIZ_Surname y después NORM_FRONT_ENG_Surname. El primer origen con valor gana y se devuelve como surname.',
      },
    },
    kycFields: {
      subtitle: 'Valores que el motor OCR extrae de un documento y qué campos estándar lee cada uno',
      usedBy: 'Usado por',
      used: 'En uso',
    },
    servers: {
      subtitle: 'Servidores de reconocimiento que procesan los documentos y si están disponibles',
      server: 'Servidor',
      portLine: 'Puerto %{port}',
      status: { Online: 'En línea', Offline: 'Sin conexión' },
      forcedOffline: 'Desconexión forzada',
      urlHint: 'Por ejemplo, https://nombre-del-servidor',
      sectionConnection: 'Conexión',
      sectionAccess: 'Acceso',
      copyKey: 'Copiar la clave API',
      keyCopied: 'Clave API copiada al portapapeles',
    },
    licenses: {
      subtitle: 'Licencias y los servidores que puede usar cada una',
      serverCount: 'Nº de servidores',
      noServers: 'Sin servidores',
      sectionLicense: 'Licencia',
      sectionServers: 'Servidores que puede usar',
    },
    users: {
      subtitle: 'Personas que pueden acceder a esta consola y su rol',
      user: 'Usuario',
      inheritedRoles: 'Permisos adicionales heredados de un grupo. Para modificarlos, gestione la pertenencia al grupo en el proveedor de identidad.',
      inheritedNotice: 'Este usuario tiene permisos adicionales heredados de un grupo. Cambiar el rol aquí modifica solo su asignación directa; la pertenencia al grupo se gestiona en el proveedor de identidad y puede mantener un rol superior efectivo.',
      createInfo: 'La cuenta se crea en el proveedor de identidad corporativo. El usuario recibirá un correo para establecer su contraseña y verificar su dirección; la aplicación no almacena ninguna contraseña.',
      fullName: 'Nombre completo',
      fullNameHint: 'Nombre y apellidos',
      emailLocked: 'El correo es el identificador único en el proveedor de identidad y no puede modificarse desde aquí.',
      sectionPerson: 'Datos del usuario',
      sectionRole: 'Rol',
    },
    backups: {
      subtitle: 'Copias programadas de la base de datos y su restauración',
    },
    costCenters: {
      subtitle: 'Grupos de cuentas que comparten un mismo saldo de créditos',
      name: 'Centro de coste',
      unpaidLimit: 'Límite impagado',
      noLimit: 'Sin límite',
      credits: '%{count} créditos',
      available: 'Créditos disponibles',
      reserved: '%{count} reservados',
      status: 'Estado',
      active: 'Activo',
      blocked: 'Bloqueado',
      block: 'Bloquear créditos',
      unblock: 'Desbloquear créditos',
      blockedDone: 'Créditos bloqueados',
      unblockedDone: 'Créditos desbloqueados',
      blockFailed: 'No se ha podido cambiar el estado',
      help: 'Cómo funciona',
      helpTitle: 'Centros de coste: cómo funcionan',
      helpIntro: 'Un centro de coste agrupa una o varias cuentas bajo una misma facturación. Cada centro tiene su propio saldo de créditos, que se consume cuando sus cuentas realizan operaciones KYC.',
      helpLimitTitle: 'Límite de crédito impagado',
      helpLimit: 'Cantidad máxima de créditos que se pueden consumir sin haber pagado. Al llegar al límite, las operaciones se bloquean.',
      helpStatusTitle: 'Estado de los créditos',
      helpStatus: 'Bloquea o desbloquea manualmente los créditos de un centro. Mientras está bloqueado, ninguna de sus cuentas puede realizar operaciones.',
      helpAccountsTitle: 'Cuentas asignadas',
      helpAccounts: 'Las cuentas asignadas a un centro de coste consumen créditos de su saldo. Una cuenta solo puede pertenecer a un centro de coste.',
      sectionDetails: 'El centro de coste',
      unpaidLimitHint: 'Máximo de créditos que se pueden usar sin pagar. 0 = sin límite.',
      accountsTitle: 'Cuentas asignadas',
      accountsHint: 'Las cuentas asignadas consumirán créditos de este centro de coste cuando realicen operaciones KYC.',
      account: 'Cuenta',
      selectAccount: 'Seleccione una cuenta',
      assign: 'Asignar',
      unassign: 'Desasignar %{name}',
      assigned: 'Cuenta asignada',
      assignFailed: 'No se ha podido asignar la cuenta',
      unassigned: 'Cuenta desasignada',
      unassignFailed: 'No se ha podido desasignar la cuenta',
      noAccounts: 'No hay cuentas asignadas a este centro de coste.',
    },
    accounts: {
      subtitle: 'Cuentas de cliente que usan el servicio, su licencia y los servidores en los que trabajan',
      status: { Active: 'Activa', Limited: 'Limitada', Blocked: 'Bloqueada' },
      logging: { None: 'Nada', Wrong: 'Peticiones fallidas', All: 'Todas las peticiones' },
      loggingLabel: 'Qué se registra',
      rateOff: 'Sin límite',
      rate: '%{min}/min, %{day}/día',
      perMin: 'Peticiones por minuto',
      perDay: 'Peticiones por día',
      rateLimitApplies: 'Aplicar límite de peticiones',
      licenseBlocked: 'Licencia bloqueada',
      apiKey: 'Clave API',
      sectionAccount: 'La cuenta',
      sectionAccess: 'Acceso',
      sectionLicense: 'Licencia',
      sectionServers: 'Servidores',
      sectionLimits: 'Límite de peticiones',
      sectionLogging: 'Registro y diagnóstico',
    },
    ocrTesting: {
      casesTitle: 'Casos de prueba',
      casesSubtitle: 'Documentos con valores conocidos con los que se comprueba el motor OCR',
      runTests: 'Ejecutar pruebas',
      newCase: 'Nuevo caso',
      runStarted: 'Ejecución de pruebas iniciada',
      runStartFailed: 'Error al iniciar la ejecución de pruebas',
      runAlreadyInProgress: 'Ya hay una ejecución de pruebas en curso. Espere a que termine.',
      loadCasesFailed: 'Error al cargar los casos de prueba',
      noCases: 'No se encontraron casos de prueba',
      noCasesHint: 'Modifique los filtros o cree un caso nuevo',
      name: 'Nombre',
      documentType: 'Tipo de documento',
      nationality: 'Nacionalidad',
      operationType: 'Tipo de operación',
      operation: 'Operación',
      status: 'Estado',
      created: 'Creado',
      all: 'Todos',
      validated: 'Validado',
      draft: 'Borrador',
      runsTitle: 'Ejecuciones',
      runsSubtitle: 'Cada ejecución comprueba los casos de prueba contra el motor OCR y registra qué coincidió',
      loadRunsFailed: 'Error al cargar las ejecuciones',
      noRuns: 'No se han realizado ejecuciones de prueba',
      noRunsHint: 'Inicie una con «Ejecutar pruebas» en Casos de prueba',
      date: 'Fecha',
      sdkVersion: 'Versión SDK',
      total: 'Total',
      passed: 'Correctos',
      failed: 'Fallidos',
      errors: 'Errores',
      failedErrors: 'Fallidos / errores',
      passRate: 'Tasa de éxito',
      passedOf: '%{rate}% (%{passed}/%{total})',
      passedOfTotal: '%{passed}/%{total} correctos',
      duration: 'Duración',
      running: 'En curso…',
      completed: 'Completada',
      runTitle: 'Ejecución del %{date}',
      runNotFound: 'Ejecución no encontrada',
      runNotFoundHint: 'Puede que se haya eliminado; vuelva a la lista de ejecuciones',
      loadResultsFailed: 'Error al cargar los resultados',
      sdkValue: 'SDK %{version}',
      durationValue: 'Duración: %{value}',
      results: 'Resultados',
      show: 'Mostrar',
      showAll: 'Todos (%{count})',
      showFailed: 'Fallidos (%{count})',
      showErrors: 'Errores (%{count})',
      noResults: 'No hay resultados que mostrar',
      noResultsHint: 'Cambie lo que muestra la lista o espere a que termine la ejecución',
      testCase: 'Caso de prueba',
      timeMs: 'Tiempo (ms)',
      timeValue: '%{ms} ms',
      showFields: 'Mostrar campos',
      hideFields: 'Ocultar campos',
      noFieldDetail: 'Sin detalle de campos disponible',
      fieldResults: 'Comparación de campos',
      field: 'Campo',
      expected: 'Esperado',
      actual: 'Leído',
      mode: 'Modo',
      result: 'Resultado',
      matched: 'Coincide',
      mismatched: 'No coincide',
      createTitle: 'Nuevo caso de prueba',
      createSubtitle: 'La imagen de un documento y los valores que el motor OCR debe leer en ella',
      caseInfo: 'Información del caso',
      info: 'Información',
      description: 'Descripción',
      nationalityHelp: 'Código de 3 letras (p. ej., ESP)',
      images: 'Imágenes',
      imagesHint: 'El anverso es obligatorio; el reverso, solo en las operaciones que leen las dos caras',
      front: 'Anverso',
      back: 'Reverso',
      dropImage: '%{side}: arrastre aquí una imagen o haga clic para seleccionarla',
      removeImage: 'Quitar',
      nameRequired: 'El nombre es obligatorio',
      frontRequired: 'Suba al menos la imagen del anverso',
      caseCreated: 'Caso de prueba creado correctamente',
      caseCreateFailed: 'Error al crear el caso de prueba',
      creating: 'Creando…',
      createCase: 'Crear caso',
      caseNotFound: 'Caso de prueba no encontrado',
      caseNotFoundHint: 'Puede que se haya eliminado; vuelva a la lista de casos',
      loadCaseFailed: 'Error al cargar el caso de prueba',
      caseUpdated: 'Caso actualizado correctamente',
      caseUpdateFailed: 'Error al actualizar el caso',
      saving: 'Guardando…',
      fieldsSaved: 'Campos guardados correctamente',
      fieldsSaveFailed: 'Error al guardar los campos',
      baselineCaptured: 'Línea base capturada: revise y corrija los valores antes de validar',
      baselineFailed: 'Error al capturar la línea base',
      caseValidated: 'Caso marcado como validado',
      validateFailed: 'Error al validar el caso',
      imageUploaded: 'Imagen subida correctamente',
      imageUploadFailed: 'Error al subir la imagen',
      uploadFront: 'Subir anverso',
      uploadBack: 'Subir reverso',
      deleteImage: 'Eliminar imagen',
      runTest: 'Ejecutar prueba',
      runningTest: 'Ejecutando…',
      runNeedsValidation: 'Valide el caso antes de ejecutarlo',
      testPassed: 'Prueba superada correctamente',
      testFailed: 'Prueba fallida: revise los campos',
      testError: 'Error durante la ejecución',
      noResultForCase: 'No se encontraron resultados para este caso',
      runTestFailed: 'Error al ejecutar la prueba',
      expectedFields: 'Campos esperados',
      captureBaseline: 'Capturar línea base',
      capturing: 'Capturando…',
      validate: 'Validar',
      saveFields: 'Guardar campos',
      baselineWarning: 'Campos capturados desde el OCR: revise y corrija los valores antes de validar.',
      fieldName: 'Nombre del campo',
      expectedValue: 'Valor esperado',
      noFields: 'No hay campos esperados',
      noFieldsHint: 'Use «Capturar línea base» o añada campos manualmente',
      addField: 'Añadir campo',
      removeField: 'Quitar campo',
      lastResult: 'Resultado: %{status}',
      fieldsMatched: '%{matched}/%{total} campos correctos',
      fixFields: 'Corregir los campos con los valores leídos',
      fieldsFromResults: 'Campos actualizados con los valores leídos: revise y guarde',
      operationTypes: { 1: 'KYC cara única', 2: 'KYC doble cara', 3: 'MRZ cara única', 4: 'MRZ doble cara', 5: 'QR MiDNI', 6: 'Comparación facial' },
      matchModes: { 1: 'Exacto', 2: 'Contiene', 3: 'Expresión regular', 4: 'No vacío' },
      resultStatuses: { 1: 'Correcto', 2: 'Fallido', 3: 'Error' },
    },
    testPage: {
      subtitle: 'Envíe un documento o una cara al servicio y vea exactamente lo que devuelve',
      tool: 'Herramienta',
      helpLabel: 'Ayuda',
      accountSection: 'Cuenta que envía',
      howToSend: 'Cómo se envía',
      captureMode: 'Modo de captura',
      transport: 'Transporte',
      transportBase64: 'Base64',
      transportS3: 'S3',
      transportFile: 'Archivo',
      whatToReturn: 'Qué se devuelve',
      dataMode: 'Modo de datos',
      dataModeHint: 'Los datos sin mapear requieren una cuenta con salida de diagnóstico',
      raw: 'Sin mapear',
      compare: 'Mapeo frente a sin mapear',
      debug: 'Depuración',
      healthCheck: 'Comprobar el servicio',
      healthFailed: 'Error al comprobar el servicio',
      healthLine: '%{role}: %{name} — %{state}',
      primary: 'Principal',
      secondary: 'Secundario',
      healthy: 'Disponible',
      down: 'Caído',
      invalidFile: 'Solo se admiten imágenes y archivos PDF',
      unknownError: 'Error desconocido',
      removeImage: 'Quitar',
      switchCamera: 'Cambiar de cámara',
      closeCamera: 'Cerrar la cámara',
      cameraDenied: 'El navegador no tiene permiso para usar la cámara. Permita el acceso a la cámara para este sitio y vuelva a intentarlo.',
      cameraNotFound: 'No se ha encontrado ninguna cámara en este equipo.',
      cameraBusy: 'La cámara está en uso por otra aplicación. Ciérrela y vuelva a intentarlo.',
      cameraUnavailable: 'Este navegador no permite usar la cámara en esta página.',
      cameraFailed: 'No se ha podido abrir la cámara.',
      retryCamera: 'Volver a intentarlo',
      result: 'Resultado',
      resultViews: 'Vistas del resultado',
      tabCount: '%{label} (%{count})',
      json: 'JSON',
      showImage: 'Mostrar imagen',
      hideImage: 'Ocultar imagen',
      resizeImagePanel: 'Arrastre para redimensionar el panel de imagen',
      compareSummary: 'Resumen',
      compareDetailMapped: 'Detalle del mapeo',
      compareDetailRaw: 'Detalle sin mapear',
      facePhoto: 'Foto de la cara del documento',
      timeOcr: 'OCR',
      timeGateway: 'Pasarela',
      timeTotal: 'Total',
      fieldsRead: 'Campos leídos',
      residence: 'Residencia',
      noResidence: 'Este tipo de documento no incluye datos de residencia',
      mapTitle: 'Mapa de la residencia',
      postalCode: 'CP %{code}',
      mappings: 'Mapeos de campos',
      mappingFields: '%{smart_count} campo |||| %{smart_count} campos',
      showMappings: 'Mostrar los mapeos',
      hideMappings: 'Ocultar los mapeos',
      noDocumentType: 'No se ha podido identificar el tipo de documento',
      noMappings: 'No hay mapeos personalizados configurados para este tipo de documento',
      score: 'Puntuación',
      yes: 'Sí',
      no: 'No',
      qrTooLarge: 'El vector es demasiado grande para un código QR',
      qrCode: 'Código QR',
      qrAlt: 'Código QR del embedding facial',
      faceMatchDistance: 'Distancia de coincidencia facial: %{value}',
      notAvailable: 'no disponible',
      compareMode: 'Qué se compara',
      responseJson: 'JSON de la respuesta',
      gender: 'Género',
      boundingBox: 'Posición de la cara',
      vectorFloats: '%{label} (%{count} floats)',
      caseCreated: 'Caso de prueba creado y validado correctamente',
      createCaseHint: 'Se creará un caso de prueba con las imágenes actuales y los campos leídos como valores esperados. El caso se marcará como validado automáticamente.',
      caseName: 'Nombre del caso',
      caseDescription: 'Descripción (opcional)',
      fieldCount: '%{smart_count} campo |||| %{smart_count} campos',
      help: {
        title: 'Test de documento: cómo funciona',
        intro: 'Pruebe el motor OCR subiendo la imagen de un documento y vea al momento los datos extraídos. Seleccione una cuenta, elija un modo de captura y pulse Procesar.',
        modesTitle: 'Modos de captura',
        single: 'Suba una imagen de una cara del documento (p. ej., el anverso de un DNI o la página de datos de un pasaporte).',
        dualCombined: 'Suba el anverso y el reverso: se combinan en una sola imagen antes de enviarlas al motor.',
        dualSeparate: 'Suba el anverso y el reverso: cada cara se envía por separado al motor y los resultados se fusionan conservando la mejor puntuación de cada campo.',
        midni: 'Para el DNI español con código QR (MiDNI). Activa la extracción del QR y la verificación de su firma digital.',
        optionsTitle: 'Opciones',
        face: 'Extrae la fotografía recortada de la cara del documento.',
        signature: 'Extrae la imagen de la firma, si la hay.',
        faceVector: 'Calcula un embedding biométrico de 512 floats de la cara para identificación. Requiere Cara.',
        mapping: 'Aplica los mapeos de campos: los nombres brutos pasan a nombres estándar. Añade una segunda llamada.',
        debug: 'Incluye en la respuesta la traza paso a paso del mapeo.',
        orientation: 'La rotación de la imagen del documento se detecta y se corrige siempre.',
        timesTitle: 'Tiempos',
        timeOcr: 'Tiempo que el motor OCR dedica a leer el documento: el coste real de la extracción. Si es alto, el cuello de botella está en el servidor OCR.',
        timeGateway: 'Tiempo del servicio que hay delante del motor OCR: autenticación, enrutamiento al servidor OCR y composición de la respuesta. La diferencia con el tiempo OCR es su sobrecarga.',
        timeTotal: 'Tiempo medido desde el navegador: la red, el servicio de administración, el procesamiento y la vuelta. La diferencia con el tiempo de la pasarela es la latencia de red y la sobrecarga de la administración.',
      },
    },
  },
  ocr_testing: { tabs: { cases: 'Casos de prueba', runs: 'Ejecuciones' } },
  users: {
    actions: {
      impersonate: 'Suplantar identidad',
    },
  },
  auth: {
    session_expired: 'Sesión caducada',
  },
  statistics: {
    last_day: 'Último día',
    last_week: 'Última semana',
    last_month: 'Último mes',
    account: 'Cuenta',
    last_access: 'Último acceso',
    total: 'Total',
    errors_col: 'Errores',
    health: 'Estado',
    prefix: 'Prefijo',
    prefix_all: 'Todos',
    no_results: 'No se encontraron estadísticas',
    rows_per_page: 'Filas por página:',
  },
  common: {
    filters: { label: 'Filtros', all: 'Todos' },
    create: 'Crear',
    more: '(+%{count} más)',
    import: 'Importar',
    importDialog: {
      title: 'Importar mapeos',
      records: 'El archivo contiene %{smart_count} registro |||| El archivo contiene %{smart_count} registros',
      replace: 'Reemplazar todos los mapeos existentes',
      replaceHint: 'Si está marcado, se eliminan todos los registros actuales y se sustituyen por los del archivo.',
      replaceWarning: 'Reemplazar: se eliminarán todos los mapeos actuales antes de importar. Esta operación no se puede deshacer.',
      mergeInfo: 'Combinar: los registros existentes se actualizan, los nuevos se añaden y el resto se mantiene sin cambios.',
      resultTitle: 'Resultado de la importación',
      total: 'Procesados',
      created: 'Creados',
      updated: 'Actualizados',
      skipped: 'Sin cambios',
      deleted: 'Eliminados',
      errors: '%{smart_count} error durante la importación |||| %{smart_count} errores durante la importación',
      errorRow: 'Fila %{row}: %{message}',
      importing: 'Importando…',
      run: 'Importar',
      runReplace: 'Reemplazar e importar',
      invalidFile: 'No se ha podido leer el archivo',
      emptyFile: 'El archivo está vacío o su formato no se reconoce',
      summary: 'Importación finalizada: %{created} creados, %{updated} actualizados, %{skipped} sin cambios, %{deleted} eliminados, %{errors} errores',
      failed: 'No se ha podido completar la importación',
    },
  },
  'default-mapping': {
    source: 'Origen',
    help_title: 'Mapeos por defecto — Cómo funciona',
    help_intro: 'Los mapeos por defecto definen qué campo KYC se utiliza para extraer un dato estándar (ej: "surname", "documentNumber"). Cuando varios campos KYC pueden proporcionar el mismo dato, la prioridad determina cuál se usa primero (0 = máxima prioridad).',
    help_naming_title: 'Convención de nombres de campos KYC',
    help_mrz: 'Datos leídos directamente de la Zona de Lectura Mecánica (las líneas codificadas en la parte inferior del documento)',
    help_viz: 'Datos leídos de la Zona de Inspección Visual (texto impreso en la cara del documento)',
    help_norm: 'Datos normalizados — procesados y estandarizados a partir de múltiples fuentes',
    help_qr: 'Datos extraídos de un código QR o de barras del documento',
    help_example: 'Ejemplo: NORM_FRONT_MRZ_Surname = Apellido extraído del MRZ del anverso, tras normalización. Prioridad 0 significa que esta fuente se intenta primero.',
  },
  'fields-mapping': {
    help_title: 'Mapeos de campos — Cómo funciona',
    help_intro: 'Los mapeos de campos definen cómo el motor OCR extrae datos de un tipo de documento y nacionalidad específicos. Cada mapeo vincula nombres de campos estándar (ej: "DocumentNumber", "Surname") a campos KYC internos, con transformaciones regex opcionales.',
    help_columns_title: 'Explicación de columnas',
    help_doc: 'Documento',
    help_doc_desc: 'El tipo de documento (ej: "Spain - Id Card (2021)") y el código de nacionalidad (ej: "ESP"). Juntos identifican de forma única qué modelo de documento aplicar.',
    help_side: 'Cara',
    help_front: '= solo el anverso del documento',
    help_back: '= solo el reverso',
    help_both: '= aplica a ambas caras (ej: imagen combinada)',
    help_fields: 'Campos estándar',
    help_fields_desc: 'La lista de nombres de campos estándar (ej: "DocumentNumber", "Surname") que este mapeo extrae. Cada campo se vincula a uno o más campos KYC internos con prefijo/sufijo y regex opcionales.',
    help_tip: 'Consejo: Use los filtros "Cara" y "Campo estándar" para encontrar rápidamente qué documentos extraen un campo específico.',
    both: {
      auto_managed_banner_title: 'Fila auto-generada',
      auto_managed_banner: 'Esta fila se genera automáticamente. Para modificarla, edite la regla en Anverso o Reverso.',
      badge_label: 'Auto-generada',
      badge_tooltip: 'Esta fila se genera a partir de Anverso y Reverso.',
      side_front: 'Anverso',
      side_back: 'Reverso',
      open_front: 'Abrir Anverso',
      open_back: 'Abrir Reverso',
      see_source: 'Ver origen',
      empty_rules: 'Sin reglas combinadas en este momento.',
      create_helper: 'La fila «Ambas caras» se genera automáticamente.',
      save_success: 'Cambios guardados. La fila «Ambas caras» se ha actualizado automáticamente.',
      priority_dialog: {
        title: 'Resolución de prioridad',
        subtitle: 'Los siguientes campos están definidos en ambas caras. Indique en qué orden deben aplicarse.',
        confirm: 'Confirmar y guardar',
        cancel: 'Cancelar',
        move_up: 'Subir',
        move_down: 'Bajar',
        regex_label: 'Regex',
        sources_label: 'Fuentes',
        conflict_hint: 'definido en Anverso y Reverso',
      },
      preview: {
        title: 'Vista previa de la fila combinada',
        loading: 'Calculando…',
        empty: 'Sin reglas proyectadas para la fila combinada.',
        conflicts_hint: 'Hay campos definidos también en la otra cara. Al guardar se le pedirá fijar el orden.',
      },
    },
  },
  accounts: {
    api_key_copied: 'API Key copiada al portapapeles',
    copy_api_key: 'Copiar',
    change_api_key: 'Modificar API Key',
    api_key_warning: 'Modificar la API Key invalidará inmediatamente la clave actual. Todas las integraciones que la utilicen dejarán de funcionar.',
    current_key: 'Clave actual',
    new_key: 'Nueva clave',
    generate_new: 'Generar otra clave',
    apply_new_key: 'Aplicar nueva clave',
    api_key_changed: 'API Key actualizada correctamente. Copie la nueva clave ahora.',
    no_api_key: 'No hay API Key disponible',
    filter_primary_server: 'Servidor principal',
    filter_secondary_server: 'Servidor secundario',
    bulk_server: {
      button: 'Reasignar servidor',
      title: 'Reasignar servidor en bloque',
      scope: 'Ámbito',
      scope_selected: 'Seleccionadas (%{n})',
      scope_filtered: 'Todas las del filtro (%{n})',
      need_scope: 'Seleccione cuentas o aplique un filtro para continuar.',
      field: 'Campo a cambiar',
      new_server: 'Nuevo servidor',
      calculating: 'Calculando…',
      preview: 'Se cambiarán %{u} cuentas. Se omitirán %{s} (quedarían con el mismo servidor como principal y secundario).',
      preview_none: 'Ninguna cuenta coincide con la selección actual.',
      apply: 'Aplicar',
      result_title: 'Resultado',
      r_matched: 'Coincidentes: %{n}',
      r_updated: 'Actualizadas: %{n}',
      r_skipped: 'Omitidas (mismo servidor en ambos campos): %{n}',
      success: 'Servidores actualizados correctamente.',
    },
  },
  test: {
    account: 'Cuenta',
    single_side: '1 cara',
    dual_combined: '2 caras (combinada)',
    dual_separate: '2 caras (separadas)',
    midni: 'MiDNI',
    face_vector: 'Vector facial',
    face: 'Cara',
    use_camera: 'Usar cámara',
    mapping: 'Mapeo',
    orientation: 'Auto-rotar',
    all_fields: 'Todos los campos',
    no_data: 'Sin datos disponibles',
    process: 'Procesar',
    processing: 'Procesando...',
    clear: 'Limpiar',
    details: 'Detalles',
    front_image: 'Imagen frontal',
    back_image: 'Imagen trasera',
    drop_image: 'Arrastre una imagen aquí o haga clic para seleccionar',
    select_account_image: 'Seleccione una cuenta y suba una imagen',
    select_account: 'Seleccione una cuenta primero',
    unknown: 'Desconocido',
    scan_again: 'Escanear de nuevo',
    new_document: 'Nuevo documento',
    create_test_case: 'Crear caso de prueba',
    mapped_data: 'Datos mapeados',
    no_mapping_data: 'No hay datos mapeados disponibles. El mapeo del gateway requiere un motor KYC real (no mock) con mapeos de campos configurados.',
    no_face_signature: 'No se extrajeron imágenes de cara o firma. El motor mock no genera imágenes.',
    mapping_debug: 'Debug de mapeo',
    face_signature: 'Cara y firma',
    field: 'Campo',
    value: 'Valor',
    source: 'Origen',
    reason: 'Razón',
    valid: 'Válido',
    language: 'Idioma',
    signature: 'Firma',
    section_ocr: 'OCR de documentos',
    section_face: 'Reconocimiento facial',
    tab_help: {
      all_fields: 'Todos los datos extraídos del documento en una lista plana, agrupados por origen (MRZ, VIZ, NORM). Cada campo muestra su nombre, valor y la puntuación de confianza del OCR (0-100%). Es la salida directa del motor antes de cualquier normalización.',
      mapped_data: 'Datos normalizados tras aplicar los mapeos de campos. Los nombres de campo estándar (ej: "FullName", "DocumentNumber") se corresponden con los campos brutos. Estos son los datos finales que recibe su integración. Solo disponible si se activó la opción "Mapeo".',
      mrz: 'Zona de Lectura Mecánica — datos decodificados de las líneas codificadas en la parte inferior del documento (las dos o tres líneas de caracteres tipo P<FRABERNARD<<MARIE<<<). Los datos MRZ son muy fiables porque siguen un estándar internacional (ICAO 9303).',
      viz: 'Zona de Inspección Visual — datos leídos del texto impreso en la cara del documento mediante OCR (reconocimiento óptico de caracteres). La precisión de los datos VIZ puede variar según la calidad de impresión, las fuentes y el estado del documento.',
      norm: 'Datos normalizados — el motor cruza los datos MRZ y VIZ, resuelve conflictos y produce un resultado limpio y estandarizado. Por ejemplo, los caracteres con tilde que faltan en el MRZ se recuperan del VIZ.',
      debug: 'Traza de mapeo que muestra cómo cada campo bruto se transformó en un campo estándar. Muestra el campo origen, el campo destino y las transformaciones regex aplicadas. Útil para diagnosticar problemas de configuración de mapeos.',
      face: 'Imágenes biométricas extraídas del documento: la fotografía facial recortada y la firma (si existe). Si se activó "Vector facial", también muestra el embedding biométrico de 512 floats como código QR descargable para identificación.',
      json: 'La respuesta JSON completa de la API, exactamente como la devuelve el motor KYC. Útil para desarrolladores que integran con la API o para diagnosticar respuestas inesperadas.',
    },
    face_recognition: {
      compare_title: 'Comparación de caras',
      mode_images: 'Dos fotos',
      mode_doc_selfie: 'Documento + Selfie',
      document: 'Documento',
      selfie: 'Selfie',
      image1: 'Imagen 1',
      image2: 'Imagen 2',
      image: 'Imagen',
      compare_btn: 'Comparar',
      embed_title: 'Extracción de embedding facial',
      embed_btn: 'Extraer embedding',
      attributes_title: 'Estimación de edad y género',
      attributes_desc: 'Endpoint ligero — estima edad y género sin calcular el embedding facial. Ideal para verificación de edad y control de acceso.',
      estimate_btn: 'Estimar',
      verified: 'Verificado — Misma persona',
      not_verified: 'No verificado — Personas distintas',
      similarity: 'Similitud',
      distance: 'Distancia coseno',
      processing_time: 'Tiempo de proceso',
      detection: 'Detección',
      age: 'Edad',
      male: 'Hombre',
      female: 'Mujer',
      estimated_age: 'Edad estimada',
      confidence: 'confianza',
      no_face_detected: 'No se detectó ninguna cara en la imagen.',
      vector_label: 'Vector de embedding',
      api_info: 'Info de integración API',
      request_body: 'Cuerpo de la petición',
      response_example: 'Ejemplo de respuesta',
      auth_note: 'Autenticación: Incluya la cabecera "Authorization: Bearer <token>" o "X-API-Key: <clave>".',
      compare_api_desc: 'Compara dos caras y devuelve una puntuación de similitud. Admite dos modos de entrada: dos fotos cualesquiera (image1 + image2) o una foto de documento frente a un selfie (document + selfie). Requiere autenticación mediante API key o token Bearer.',
      embed_api_desc: 'Extrae un embedding facial de 512 dimensiones de una imagen. El embedding puede almacenarse y compararse posteriormente mediante distancia coseno, o enviarse al endpoint /compare como vector pre-calculado.',
      attributes_api_desc: 'Estima edad y género a partir de una imagen facial. No calcula el embedding, por lo que es más rápido que /embed. Admite procesamiento individual o por lotes.',
      download_qr: 'Descargar imagen QR',
      capture: 'Capturar',
    },
  },
  dashboard: {
    heading: 'Panel de la plataforma KYC',
    systemResources: 'Recursos del sistema',
    health: { healthy: 'Correcto', warning: 'Advertencia', critical: 'Crítico' },
    kpi: {
      totalAccounts: 'Cuentas totales',
      requestsToday: 'Solicitudes hoy',
      requestsThisWeek: 'solicitudes esta semana',
      errorRate: 'Tasa de error hoy',
      mostActive: 'Más activa',
      serversConfigured: 'servidores configurados',
    },
    chart: {
      success: 'Correctas',
      errors: 'Errores',
      avgDaily: 'Media diaria (7 días)',
      weekTotal: 'Total semanal',
      successRate: 'Tasa de acierto',
    },
    activity: {
      title: 'Actividad de cuentas',
      account: 'Cuenta',
      health: 'Estado',
      todaySuccessError: 'Hoy (Correctas / Errores)',
      week: 'Semana',
      month: 'Mes',
      lastAccess: 'Último acceso',
      never: 'Nunca',
    },
    resources: {
      accounts: 'Cuentas',
      servers: 'Servidores',
      licenses: 'Licencias',
      docTypes: 'Tipos de doc.',
      nationalities: 'Nacionalidades',
      kycFields: 'Campos KYC',
      users: 'Usuarios',
      mappings: 'Mapeos',
      defaults: 'Por defecto',
      costCenters: 'Centros de coste',
    },
    tabs: { panel: 'Panel', },
  },
  tech_support: {
    tabs: {
      overview: 'Vista general',
      servers: 'Servidores KYC',
      stress_test: 'Test de estrés',
    },
    kpi: {
      gateway: 'API Gateway',
      kycServices: 'Servicios KYC',
      serversOnline: 'servidores activos',
      accounts: 'Cuentas',
      errorRate: 'Tasa de error',
    },
    gateway_health: {
      title: 'Estado del gateway',
      unavailable: 'El estado detallado no está disponible. Asegúrese de que haya al menos una cuenta configurada.',
      ocr_available: 'OCR disponible — el escaneo de documentos está operativo',
      ocr_unavailable: 'OCR no disponible — ningún servidor OCR responde, escaneo de documentos desactivado',
      ocr_servers: 'Servidores OCR',
      no_ocr_servers: 'No hay servidores OCR configurados',
      local_capabilities: 'Capacidades locales',
      facematch_tooltip: 'La comparación de caras se procesa localmente en el gateway, no depende de los servidores OCR',
      midni_tooltip: 'La decodificación del QR del miDNI se procesa localmente en el gateway, no depende de los servidores OCR',
    },
    system_info: {
      title: 'Información del sistema',
      version: 'Versión',
      environment: 'Entorno',
      gatewayUrl: 'URL del gateway',
    },
    health_checks: {
      title: 'Comprobaciones de estado',
      service: 'Servicio',
      status: 'Estado',
      latency: 'Latencia',
    },
    servers: {
      empty: 'No hay servidores registrados',
      name: 'Nombre',
      url: 'URL',
      status: 'Estado',
      latency: 'Latencia',
      actions: 'Acciones',
      ping: 'Medir tiempo de respuesta',
      forcedOffline: 'Forzado desconexión',
    },
    stress: {
      disclaimer: 'Todos los tests de estrés se ejecutan desde el servidor del Admin API y se enrutan a través del API Gateway, nunca directamente a los servicios KYC. Esto proporciona mediciones realistas de concurrencia y latencia que reflejan los flujos de peticiones reales en producción.',
      config_title: 'Configuración del test',
      scenarios: { gateway_health: 'Health Check del Gateway', admin_api: 'Consulta Admin API', ocr: 'Procesamiento OCR' },
      scenario: 'Escenario',
      account: 'Cuenta',
      concurrent: 'Peticiones concurrentes',
      duration: 'Duración',
      start: 'Iniciar test',
      cancel: 'Cancelar',
      progress_title: 'Progreso',
      completed: 'Completadas',
      errors: 'Errores',
      avg_latency: 'Latencia media',
      results_title: 'Resultados',
      error_rate: 'Tasa de error',
      latency_distribution: 'Distribución de latencia',
      total_requests: 'Peticiones totales',
      successful: 'Correctas',
      failed: 'Fallidas',
      error_breakdown: 'Desglose de errores',
      count: 'Cantidad',
      message: 'Mensaje',
      test_image: 'Imagen de prueba (documento)',
      upload_image: 'Seleccionar imagen de documento...',
      image_loaded: 'Imagen cargada',
      image_ready: 'Imagen lista — se enviará con cada petición',
      placeholder: 'Configure e inicie un test de estrés para ver los resultados aquí',
      history_title: 'Historial de tests',
      history_date: 'Fecha',
    },
  },
};

/* ─── Provider ─── */
const messages: Record<string, any> = { en, es };

export const i18nProvider = polyglotI18nProvider(
  (locale: string) => messages[locale] || es,
  // The interface is written for Spain Spanish; English is the reader's choice.
  'es',
  [
    { locale: 'en', name: 'English' },
    { locale: 'es', name: 'Español' },
  ]
);

export default i18nProvider;
